import React, { useState, useEffect, useRef, useMemo } from "react";
import SettingTooltip, { WEBSITE_CONTACT_TOGGLE_TOOLTIP_TEXT } from "./components/SettingTooltip";
import GroundingToggle from "./components/GroundingToggle";
import axios from "axios";
import apiClient from "./api/config";
import {
  FiSave,
  FiRefreshCw,
  FiXCircle,
  FiGlobe,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiEdit3,
  FiCopy,
  FiSend,
  FiFlag,
  FiPlus,
  FiPackage,
  FiSearch,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getDatabase, ref, get, update, onValue, set, off } from "firebase/database";
import PointWarning from "./components/PointWarning";
import {
  WORDPRESS_POST_LENGTHS,
  DEFAULT_WORDPRESS_POST_LENGTH,
  POST_STYLES,
  DEFAULT_POST_STYLES,
  CONTENT_FORMAT_OPTIONS,
  DEFAULT_CONTENT_FORMAT,
} from "./constants/postSettings";
import PostLanguageSelector from "./components/PostLanguageSelector";
import { getLanguageName } from "./constants/languages";
import { ClipLoader } from "react-spinners";
import { sendPostReportNotification } from "./utils/telegramNotification";
import { postHistoryService } from "./services/postHistoryService";
import {
  compressImage,
  validateImageUrl,
  formatFileSize,
} from "./utils/imageCompressionUtil";
import {
  IMAGE_SOURCES,
  IMAGE_SOURCE_LABELS,
  DEFAULT_IMAGE_SOURCE,
  IMAGE_STYLE_OPTIONS,
  DEFAULT_IMAGE_STYLE,
  ASPECT_RATIO_OPTIONS,
  DEFAULT_ASPECT_RATIO,
  WP_IMAGE_COUNT_OPTIONS,
  DEFAULT_WP_IMAGE_COUNT,
} from "./services/aiImageService";
import {
  getSelectedWooProducts,
  mergeReferenceUrls,
  requiresWooProductSelection,
} from "./utils/websiteProductSeo";
import { fetchAllWooProductsForSite } from "./utils/wooProductsApi";
import { normalizeWordPressSiteUrl } from "./utils/wordpressUrl";
import { checkSeoConnector } from "./api/wordpressConnector";

const WEBSITE_TOPIC_ASPECT_RATIO = "3:2";
const MAX_USER_IMAGES = 5;
const WEBSITE_IMAGE_SOURCE_LABELS = {
  ...IMAGE_SOURCE_LABELS,
  [IMAGE_SOURCES.UPLOAD]: "🖼️ Ảnh tự cung cấp",
};
const WEBSITE_TOPIC_ASPECT_RATIO_OPTIONS = ASPECT_RATIO_OPTIONS.map((opt) =>
  opt.value === "16:9"
    ? {
        ...opt,
        value: WEBSITE_TOPIC_ASPECT_RATIO,
        desc: "3:2 — Bài viết",
      }
    : opt
);

const resolveWebsiteAspectRatio = ({ aspectRatio, imageSource }) => {
  if (imageSource !== IMAGE_SOURCES.AI) {
    return aspectRatio || DEFAULT_ASPECT_RATIO;
  }

  if (
    !aspectRatio ||
    aspectRatio === "16:9" ||
    aspectRatio === DEFAULT_ASPECT_RATIO
  ) {
    return WEBSITE_TOPIC_ASPECT_RATIO;
  }

  return aspectRatio;
};

const getWebsitePostCostPoints = ({ imageSource, useWooProducts, useGrounding = false }) => {
  // Premium Grounding: +10 điểm khi user bật Google Search opt-in
  const groundingExtra = useGrounding ? 10 : 0;

  if (useWooProducts) {
    return 25 + groundingExtra;
  }

  return (imageSource === IMAGE_SOURCES.AI ? 100 : 25) + groundingExtra;
};

const AUTOWORK_IMAGE_PLACEHOLDER_REGEX =
  /autowork-image-placeholder|AUTOWORK_IMAGE_SLOT_/i;

const isRenderableImageSource = (value) =>
  typeof value === "string" &&
  (/^data:image\//i.test(value) ||
    /^https?:\/\//i.test(value) ||
    /^blob:/i.test(value) ||
    /^\/\//.test(value));

const escapeHtmlAttribute = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const normalizeGeneratedImageSources = (images) => {
  if (!Array.isArray(images)) return [];

  return images
    .map((image) => (typeof image === "string" ? image.trim() : ""))
    .filter(isRenderableImageSource);
};

const hasAutoworkImagePlaceholders = (content) =>
  AUTOWORK_IMAGE_PLACEHOLDER_REGEX.test(content || "");

const hasRenderableInlinePostImages = (content) =>
  /<img\b[^>]*src=["'](?:data:image\/|https?:\/\/|blob:|\/\/)/i.test(
    content || ""
  );

const countRenderableInlinePostImages = (content) =>
  (
    content?.match(
      /<img\b[^>]*src=["'](?:data:image\/|https?:\/\/|blob:|\/\/)/gi
    ) || []
  ).length;

const countAutoworkImagePlaceholders = (content) =>
  (content?.match(/data-autowork-image-slot=["']\d+["']/gi) || []).length;

const createPreviewImageHtml = (imageSrc, imageIndex, title = "") => {
  const altText =
    title?.trim() || `Hình minh họa ${typeof imageIndex === "number" ? imageIndex + 1 : 1}`;

  return `
<figure class="wp-block-image size-large">
  <img src="${escapeHtmlAttribute(imageSrc)}" alt="${escapeHtmlAttribute(
    altText
  )}" class="wp-image" decoding="async" loading="${
    imageIndex === 0 ? "eager" : "lazy"
  }" />
</figure>`;
};

const hydrateAutoworkImagePlaceholders = (content, images = [], title = "") => {
  if (!content || !hasAutoworkImagePlaceholders(content)) {
    return content || "";
  }

  const normalizedImages = normalizeGeneratedImageSources(images);
  let hydratedContent = content;

  normalizedImages.forEach((imageSrc, imageIndex) => {
    const previewImageHtml = createPreviewImageHtml(imageSrc, imageIndex, title);
    const slotRegex = new RegExp(
      `<figure\\b[^>]*data-autowork-image-slot=["']${imageIndex}["'][\\s\\S]*?<\\/figure>`,
      "i"
    );
    const commentRegex = new RegExp(
      `<!--\\s*AUTOWORK_IMAGE_SLOT_${imageIndex}\\s*-->`,
      "i"
    );

    if (slotRegex.test(hydratedContent)) {
      hydratedContent = hydratedContent.replace(slotRegex, previewImageHtml);
      return;
    }

    if (commentRegex.test(hydratedContent)) {
      hydratedContent = hydratedContent.replace(commentRegex, previewImageHtml);
    }
  });

  return hydratedContent
    .replace(
      /<figure\b[^>]*autowork-image-placeholder[^>]*>[\s\S]*?<\/figure>/gi,
      ""
    )
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const countPostImagesForPublish = (post) => {
  const generatedImageCount = normalizeGeneratedImageSources(post?.images).length;
  if (!post?.text) return generatedImageCount;

  const inlineImageCount = countRenderableInlinePostImages(post.text);
  const placeholderImageCount = hasAutoworkImagePlaceholders(post.text)
    ? Math.min(countAutoworkImagePlaceholders(post.text), generatedImageCount)
    : 0;

  return Math.max(inlineImageCount, generatedImageCount, placeholderImageCount);
};

const hasInlinePostImages = (content) => hasRenderableInlinePostImages(content);

const WebsitePostGenerator = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const styleDropdownRef = useRef(null);
  const loadingCategoriesSiteIdRef = useRef(null);

  // Function to process content for preview - lighter processing for already formatted content
  const processContentForPreview = (content, images = [], title = "") => {
    if (!content) return "";

    const hydratedContent = hasAutoworkImagePlaceholders(content)
      ? hydrateAutoworkImagePlaceholders(content, images, title)
      : content;

    // Check if content already has structured HTML (tables, images, etc.)
    const hasStructuredHtml =
      hydratedContent.includes('<figure class="wp-block-image') ||
      hydratedContent.includes("<img") ||
      /<table[\s>]/i.test(hydratedContent);

    if (hasStructuredHtml) {
      console.log(
        "✅ Content has structured HTML (images/tables) - minimal processing"
      );
      // Content already has structured formatting - just do basic cleanup
      return hydratedContent
        .replace(/\n{3,}/g, "\n\n") // Normalize line breaks
        .replace(/<p>\s*<\/p>/g, "") // Remove empty paragraphs
        .trim();
    } else {
      console.log(
        "🔄 Content doesn't have structured HTML - using full processing"
      );
      // Use full processing for content without structured HTML
      return processPostContent(hydratedContent);
    }
  };

  // Function to process post content for WordPress-compatible display
  const processPostContent = (content) => {
    if (!content) return "";

    // Step 0: Extract and preserve HTML table blocks before any processing
    const tableBlocks = [];
    let contentWithoutTables = content.replace(
      /<table[\s\S]*?<\/table>/gi,
      (match) => {
        const placeholder = `__TABLE_BLOCK_${tableBlocks.length}__`;
        tableBlocks.push(match);
        return placeholder;
      }
    );

    // Step 1: First, extract and store image URLs to process them separately
    const imageUrls = [];
    const imageMap = new Map();

    // Find all image URLs and replace them with placeholders
    let processedContent = contentWithoutTables.replace(
      /(https?:\/\/[^\s<>"]+\.(jpg|jpeg|png|gif|webp|svg)(?:\?[^\s<>"]*)?)/gi,
      (match, url) => {
        const placeholder = `__IMAGE_PLACEHOLDER_${imageUrls.length}__`;
        imageUrls.push(url);
        imageMap.set(placeholder, url);
        return placeholder;
      }
    );

    // Debug logging để theo dõi quá trình xử lý ảnh
    if (imageUrls.length > 0) {
      console.log(
        `🖼️ Phát hiện ${imageUrls.length} ảnh trong nội dung:`,
        imageUrls
      );
    }

    // Step 2: Clean up and normalize content (excluding images which are now placeholders)
    processedContent = processedContent
      // Remove excessive line breaks (more than 2 consecutive \n)
      .replace(/\n{3,}/g, "\n\n")
      // Normalize line breaks
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      // Clean up any malformed HTML attributes that might appear in content
      .replace(/class=["'][^"']*["']/g, "")
      .replace(/srcset=["'][^"']*["']/g, "")
      .replace(/sizes=["'][^"']*["']/g, "")
      .replace(/onerror=["'][^"']*["']/g, "")
      .replace(/src=["'][^"']*["']/g, "")
      .replace(/style=["'][^"']*["']/g, "")
      .replace(/alt=["'][^"']*["']/g, "")
      .replace(/width=["'][^"']*["']/g, "")
      .replace(/height=["'][^"']*["']/g, "")
      // Remove standalone HTML attributes without quotes
      .replace(
        /\b(class|srcset|sizes|onerror|src|style|alt|width|height)=\S*/g,
        ""
      )
      // Remove orphaned closing tags and fragments
      .replace(/<\/\s*>/g, "")
      .replace(/^\s*["'>]\s*$/gm, "")
      // Remove HTML tag closing fragments like ');"; />' or '/>' or ';" />'
      .replace(/\s*['");]*\s*\/\s*>\s*/g, "")
      .replace(/^\s*\)\s*;\s*["']?\s*\/\s*>\s*$/gm, "")
      .replace(/^\s*["']?\s*\/\s*>\s*$/gm, "")
      // Clean up multiple spaces and orphaned quotes
      .replace(/["']{2,}/g, "")
      .replace(/\s{3,}/g, " ");

    // Split content into paragraphs and process each
    const paragraphs = processedContent.split(/\n\s*\n/);
    const processedParagraphs = [];

    for (let paragraph of paragraphs) {
      // Preserve table block placeholders as-is
      const trimmedParagraph = paragraph.trim();
      if (/^__TABLE_BLOCK_\d+__$/.test(trimmedParagraph)) {
        processedParagraphs.push(trimmedParagraph);
        continue;
      }

      const lines = paragraph
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => {
          // Filter out lines that are just HTML attributes or fragments
          return (
            line &&
            !line.match(/^\s*["']?\s*$/) && // Empty or just quotes
            !line.match(/^\s*\d+w\s*,?\s*$/) && // srcset values like "960w,"
            !line.match(/^\s*\(/) && // sizes values like "(max-width: 767px)"
            !line.match(
              /^\s*(class|srcset|sizes|onerror|src|style|alt|width|height)\s*=/
            ) && // HTML attributes
            !line.match(/^\s*\/\s*>\s*$/) && // Closing tag fragments
            !line.match(/^\s*["'>]\s*$/) && // Quote or bracket fragments
            !line.match(/^\s*\)\s*;\s*["']?\s*\/\s*>\s*$/) && // HTML closing fragments like ');" />'
            !line.match(/^\s*['");]+\s*\/\s*>\s*$/) && // Other closing tag variations
            line.length > 2 // Minimum content length
          );
        });

      if (lines.length === 0) continue;

      // Check if this is a list section
      const listItems = lines.filter(
        (line) => line.match(/^[-•*]\s+/) || line.match(/^\d+\.\s+/)
      );

      if (listItems.length > 0 && listItems.length === lines.length) {
        // This is a complete list
        const isNumbered = lines[0].match(/^\d+\.\s+/);
        const listTag = isNumbered ? "ol" : "ul";

        let listHtml = `<${listTag}>`;
        for (let line of lines) {
          const itemText = line.replace(/^[-•*\d+\.\s]+/, "").trim();
          listHtml += `<li>${itemText}</li>`;
        }
        listHtml += `</${listTag}>`;
        processedParagraphs.push(listHtml);
      } else {
        // Process as regular paragraphs and headings
        for (let line of lines) {
          // Skip lines that look like HTML attributes or fragments
          if (
            line.match(
              /^\s*(class|srcset|sizes|onerror|src|style|alt|width|height)\s*=/
            ) ||
            line.match(/^\s*["'<>\s]*$/) ||
            line.match(/^\s*\/\s*>\s*$/) ||
            line.match(/^\s*\d+w\s*,?\s*$/) ||
            line.match(/^\s*\(/) ||
            line.match(/^\s*\)\s*;\s*["']?\s*\/\s*>\s*$/) || // HTML closing fragments
            line.match(/^\s*['");]+\s*\/\s*>\s*$/) // Other closing variations
          ) {
            continue;
          }

          // Check if this is a heading
          if (line.match(/^#+\s+/)) {
            const headingLevel = Math.min(
              (line.match(/^#+/) || [""])[0].length,
              6
            );
            const headingText = line.replace(/^#+\s+/, "").trim();
            if (headingText) {
              processedParagraphs.push(
                `<h${headingLevel}>${headingText}</h${headingLevel}>`
              );
            }
          } else if (line.match(/^[-•*]\s+/) || line.match(/^\d+\.\s+/)) {
            // Single list item - convert to paragraph
            const itemText = line.replace(/^[-•*\d+\.\s]+/, "").trim();
            if (itemText) {
              processedParagraphs.push(`<p>• ${itemText}</p>`);
            }
          } else {
            // Regular paragraph - but skip if it's just HTML fragments or already processed images
            const cleanLine = line.trim();
            if (
              cleanLine &&
              cleanLine.length > 2 &&
              !cleanLine.includes("__IMAGE_PLACEHOLDER_") &&
              !cleanLine.includes("__TABLE_BLOCK_")
            ) {
              processedParagraphs.push(`<p>${cleanLine}</p>`);
            }
          }
        }
      }
    }

    // Join all processed paragraphs
    let finalContent = processedParagraphs.join("\n\n");

    // Step 3: Now restore image placeholders with proper WordPress image blocks
    let restoredImageCount = 0;
    for (const [placeholder, url] of imageMap) {
      // Escape special regex characters in placeholder
      const escapedPlaceholder = placeholder.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

      // Only replace if the placeholder still exists (avoid double processing)
      if (finalContent.includes(placeholder)) {
        finalContent = finalContent.replace(
          new RegExp(escapedPlaceholder, "g"),
          `<figure class="wp-block-image size-large"><img src="${url}" alt="" class="wp-image" /></figure>`
        );
        restoredImageCount++;
      }
    }

    // Debug logging cho việc khôi phục ảnh
    if (imageUrls.length > 0) {
      console.log(
        `✅ Đã khôi phục ${restoredImageCount}/${imageUrls.length} ảnh vào nội dung WordPress`
      );
    }

    // Step 4: Additional cleanup to remove any remaining orphaned image tags or duplicate figures
    finalContent = finalContent
      // Remove duplicate consecutive figure blocks
      .replace(
        /(<\/figure>)\s*(<figure[^>]*>\s*<img[^>]*src="[^"]*"[^>]*>\s*<\/figure>)\s*\2/g,
        "$1\n\n$2"
      )
      // Remove empty figures
      .replace(/<figure[^>]*>\s*<\/figure>/g, "");

    // Step 5: Restore preserved table blocks
    tableBlocks.forEach((tableHtml, index) => {
      finalContent = finalContent.replace(
        `__TABLE_BLOCK_${index}__`,
        tableHtml
      );
    });

    // Clean up WordPress-style formatting
    finalContent = finalContent
      .replace(/<p>\s*<\/p>/g, "") // Remove empty paragraphs
      .replace(/<p><\/p>/g, "") // Remove empty paragraphs
      .replace(/<p>\s*<\/p>/g, "") // Remove paragraphs with only whitespace
      .replace(/\n{3,}/g, "\n\n") // Normalize line breaks
      .replace(/\s+/g, " ") // Normalize spaces but preserve single spaces
      .trim();

    return finalContent;
  };

  // Function to create WordPress-style preview CSS
  const getWordPressPreviewStyles = () => {
    return {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
      fontSize: "16px",
      lineHeight: "1.6",
      color: "#333333",
      maxWidth: "100%",
    };
  };

  // Function to fetch WordPress theme CSS
  const fetchWordPressThemeCSS = async (siteUrl) => {
    if (!siteUrl || isLoadingThemeCSS) return;

    setIsLoadingThemeCSS(true);
    try {
      // Clean URL
      const baseUrl = normalizeWordPressSiteUrl(siteUrl);

      // Try to fetch theme info and CSS
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/content/fetch-wordpress-theme-css`,
        {
          params: { siteUrl: baseUrl },
          timeout: 10000,
        }
      );

      if (response.data.success) {
        setThemeCSS(response.data.css || "");
        setThemeInfo(response.data.themeInfo || null);
      } else {
        // Fallback to basic WordPress CSS
        setThemeCSS("");
        setThemeInfo(null);
      }
    } catch (error) {
      console.error("Error fetching WordPress theme CSS:", error);
      // Fallback to default styling
      setThemeCSS("");
      setThemeInfo(null);
    } finally {
      setIsLoadingThemeCSS(false);
    }
  };

  // Function to extract and clean WordPress theme CSS for content area
  const getContentAreaCSS = (fullCSS) => {
    if (!fullCSS) return "";

    // Extract content-related CSS rules
    const contentSelectors = [
      ".entry-content",
      ".post-content",
      ".content",
      "article",
      ".single-post",
      ".post",
      "p",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "blockquote",
      "img",
      ".wp-block-image",
      "figure",
      "table",
      "thead",
      "tbody",
      "th",
      "td",
      "tr",
      ".wp-block-table",
    ];

    // Simple CSS rule extraction (basic implementation)
    const lines = fullCSS.split("\n");
    const contentCSS = [];
    let inContentRule = false;
    let braceCount = 0;

    for (let line of lines) {
      const trimmedLine = line.trim();

      // Check if this line starts a content-related rule
      if (
        contentSelectors.some(
          (selector) =>
            trimmedLine.includes(selector) && trimmedLine.includes("{")
        )
      ) {
        inContentRule = true;
        braceCount = (trimmedLine.match(/{/g) || []).length;
        contentCSS.push(line);
        continue;
      }

      if (inContentRule) {
        contentCSS.push(line);
        braceCount += (trimmedLine.match(/{/g) || []).length;
        braceCount -= (trimmedLine.match(/}/g) || []).length;

        if (braceCount <= 0) {
          inContentRule = false;
          braceCount = 0;
        }
      }
    }

    return contentCSS.join("\n");
  };

  // State management
  const [requirements, setRequirements] = useState("");
  const [postLength, setPostLength] = useState(DEFAULT_WORDPRESS_POST_LENGTH);
  const [postStyles, setPostStyles] = useState(DEFAULT_POST_STYLES);
  const [userPoint, setUserPoint] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("vi");
  const [userMembership, setUserMembership] = useState(null);
  const [websitePostQuantity, setWebsitePostQuantity] = useState(0);

  // WordPress configuration
  const [wordpressSites, setWordpressSites] = useState([]);
  const [selectedWordpressSite, setSelectedWordpressSite] = useState(null);
  const [isWordpressSiteDropdownOpen, setIsWordpressSiteDropdownOpen] =
    useState(false);
  const [wordpressConfig, setWordpressConfig] = useState({
    url: "",
    username: "",
    password: "",
    categories: [1], // Default to "Tin tức" category (ID: 1)
    tags: "",
    status: "publish",
  });
  const [availableCategories, setAvailableCategories] = useState([
    { id: 1, name: "Tin tức" },
    { id: 2, name: "Sản phẩm" },
    { id: 3, name: "Khuyến mãi" },
    { id: 4, name: "Công nghệ" },
  ]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  // categoryMode: 'disabled' (tắt) hoặc 'manual' (chọn/nhập tay) hoặc 'ai' (AI tự chọn sau khi đăng)
  const [categoryMode, setCategoryMode] = useState("disabled");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [useWooProducts, setUseWooProducts] = useState(false);
  const [allowWooProductLinks, setAllowWooProductLinks] = useState(true);
  const [wooProducts, setWooProducts] = useState([]);
  const [selectedWooProductIds, setSelectedWooProductIds] = useState([]);
  const [isLoadingWooProducts, setIsLoadingWooProducts] = useState(false);
  const [wooProductsError, setWooProductsError] = useState("");
  const [hasLoadedWooProducts, setHasLoadedWooProducts] = useState(false);
  const [wooProductSearchQuery, setWooProductSearchQuery] = useState("");
  const [wooProductsMeta, setWooProductsMeta] = useState({
    count: 0,
    total: 0,
    totalPages: 0,
    fetchedAll: false,
    source: "",
  });
  const [allowNoImage, setAllowNoImage] = useState(false);
  const [imageSource, setImageSource] = useState(DEFAULT_IMAGE_SOURCE);
  const [imageStyle, setImageStyle] = useState(DEFAULT_IMAGE_STYLE);
  const [aspectRatio, setAspectRatio] = useState(
    resolveWebsiteAspectRatio({
      aspectRatio: DEFAULT_ASPECT_RATIO,
      imageSource: DEFAULT_IMAGE_SOURCE,
    })
  );
  const [customImagePrompt, setCustomImagePrompt] = useState("");
  const [useGoogleSearch, setUseGoogleSearch] = useState(false); // Premium Grounding opt-in, default OFF
  const [imageValidationEnabled, setImageValidationEnabled] = useState(true);
  const [wpImageCount, setWpImageCount] = useState(DEFAULT_WP_IMAGE_COUNT);
  const [isStyleDropdownOpen, setIsStyleDropdownOpen] = useState(false);

  // Content format state
  const [contentFormat, setContentFormat] = useState(DEFAULT_CONTENT_FORMAT);

  // Post generation states
  const [isGeneratingPost, setIsGeneratingPost] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPostText, setEditedPostText] = useState("");
  const [hasEdited, setHasEdited] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // WordPress theme CSS states
  const [themeCSS, setThemeCSS] = useState("");
  const [isLoadingThemeCSS, setIsLoadingThemeCSS] = useState(false);
  const [themeInfo, setThemeInfo] = useState(null);

  // Topic suggestion states
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [isSuggestingTopics, setIsSuggestingTopics] = useState(false);

  // Website information usage state
  const [useWebsiteInfo, setUseWebsiteInfo] = useState(false);

  // Contact info append state (WordPress)
  const [includeContactInfo, setIncludeContactInfo] = useState(false);

  // URL context states
  const [inputUrls, setInputUrls] = useState("");
  const [urlValidationErrors, setUrlValidationErrors] = useState([]);
  // @DEPRECATED: isLoadingImages không còn cần thiết vì ảnh được xử lý ở backend
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // User images states (max 5 ảnh)
  const [userImages, setUserImages] = useState([]);
  const [imageInputUrl, setImageInputUrl] = useState("");
  const [imageInputErrors, setImageInputErrors] = useState([]);
  const [isCompressingImage, setIsCompressingImage] = useState(false);

  // Report states
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Job states
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobError, setJobError] = useState(null);
  const requiredWebsitePostPoints = getWebsitePostCostPoints({
    imageSource,
    useWooProducts,
    useGrounding: useGoogleSearch === true,
  });
  // 5 lượt viết bài website MIỄN PHÍ chỉ dành cho user ở gói Trải nghiệm (membership === 6).
  // User gói Cơ bản trở lên (1, 2, 3, 4) hoặc không có plan KHÔNG được dùng free.
  const hasFreeWebsiteQuota =
    userMembership === 6 && websitePostQuantity > 0;

  const websitePostChargeLabel = hasFreeWebsiteQuota
    ? "0 điểm"
    : `${requiredWebsitePostPoints} điểm`;

  // Publish job states
  const [currentPublishJobId, setCurrentPublishJobId] = useState(null);
  const [publishJobStatus, setPublishJobStatus] = useState(null);
  const [publishJobProgress, setPublishJobProgress] = useState(0);
  const [publishJobError, setPublishJobError] = useState(null);


  // Post history states
  const [currentHistoryId, setCurrentHistoryId] = useState(null);

  // Loading user data
  // OPTIMIZATION: Added cleanup for all Firebase listeners to prevent memory leak and reduce bandwidth
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const db = getDatabase();

      // Load user points
      const pointsRef = ref(db, `/users/${userId}/points`);
      const unsubscribePoints = onValue(pointsRef, (snapshot) => {
        const pointsData = snapshot.val() || {};
        const points = pointsData.points || 0;
        const purchasedPoints = pointsData.purchasedPoints || 0;
        setUserPoint(points + purchasedPoints);
      });

      // Load membership data
      const membershipRef = ref(db, `/users/${userId}/membership`);
      const unsubscribeMembership = onValue(membershipRef, (snapshot) => {
        // Normalize membership to number — admin PATCH may store as string "3"
        let membershipVal = snapshot.exists() ? snapshot.val() : null;
        if (typeof membershipVal === "string") {
          const parsed = parseInt(membershipVal, 10);
          membershipVal = isNaN(parsed) ? membershipVal : parsed;
        }
        setUserMembership(membershipVal || 1);
      });

      // Load website post quantity
      const websitePostRef = ref(db, `/users/${userId}/websitePostQuantity`);
      const unsubscribeWebsitePost = onValue(websitePostRef, (snapshot) => {
        setWebsitePostQuantity(snapshot.val() || 0);
      });

      // Load WordPress sites
      loadWordPressSites();

      // Cleanup all listeners when component unmounts
      return () => {
        unsubscribePoints();
        unsubscribeMembership();
        unsubscribeWebsitePost();
      };
    }
  }, []);

  // Job listener effect
  useEffect(() => {
    if (!currentJobId) return;

    const db = getDatabase();
    const jobRef = ref(db, `jobs/${currentJobId}`);

    console.log(`👂 Listening to job ${currentJobId}`);

    const unsubscribe = onValue(jobRef, (snapshot) => {
      const jobData = snapshot.val();

      if (!jobData) {
        console.log(`❌ Job ${currentJobId} not found`);
        setJobError("Job không tồn tại");
        return;
      }

      console.log(
        `📊 Job ${currentJobId} status:`,
        jobData.status,
        `(${jobData.progress}%)`
      );

      setJobStatus(jobData.status);
      setJobProgress(jobData.progress || 0);

      if (jobData.status === "completed" && jobData.result) {
        // Job hoàn thành thành công
        console.log(`✅ Job ${currentJobId} completed successfully`);

        const result = jobData.result;
        const generatedImages = normalizeGeneratedImageSources(result.images);
        const inlineImageCount = (result.content?.match(/<img|<figure/g) || [])
          .length;

        // Debug log cho meta description
        console.log("📋 Result from backend:", {
          hasMetaDescription: !!result.metaDescription,
          metaDescLength: result.metaDescription?.length || 0,
          metaDescPreview: result.metaDescription?.substring(0, 80) || "N/A",
          imagesCount: generatedImages.length,
        });

        setGeneratedPost({
          title: result.title,
          text: result.content,
          images: generatedImages,
          metaDescription: result.metaDescription, // Lưu meta description từ AI
          focusKeyphrase: result.focusKeyphrase, // Lưu focus keyphrase từ AI cho Yoast SEO
          seoTitle: result.seoTitle,
          slug: result.slug,
          schemaJsonLd: result.schemaJsonLd,
          seoTags: result.seoTags || [], // Lưu SEO tags từ AI để gán tags trên WordPress
          yoastReadiness: result.yoastReadiness || null,
          metadata: {
            pointsDeducted: result.pointsDeducted || false,
            pointsDeductedAmount: result.pointsDeductedAmount || 0,
            pointsRefunded: false,
            usedFreeQuantity: result.usedFreeQuantity || false,
          },
        });

        setEditedPostText(result.content);
        setIsEditMode(false);
        setHasEdited(false);
        setIsGeneratingPost(false);

        // Cập nhật lịch sử khi thành công
        if (currentHistoryId) {
          const updateData = {
            status: "success",
            postData: {
              title: result.title,
              content: result.content,
              contentLength: result.content?.length || 0,
              imagesCount: generatedImages.length,
            },
            pointsData: {
              pointsDeducted: result.pointsDeducted || false,
              pointsDeductedAmount: result.pointsDeductedAmount || 0,
              pointsRefunded: false,
              pointsRefundedAmount: 0,
              usedFreeQuantity: result.usedFreeQuantity || false,
              userMembership,
            },
            stats: {
              imageCount: Math.max(inlineImageCount, generatedImages.length),
              estimatedReadTime: Math.ceil(
                (result.content?.length || 0) / 1000
              ),
              wordCount: Math.ceil((result.content?.length || 0) / 5),
            },
          };

          postHistoryService.updateHistory(currentHistoryId, updateData);
          console.log("✅ Đã cập nhật lịch sử thành công");
        }

        // Reset job states
        setCurrentJobId(null);
        setJobStatus(null);
        setJobProgress(0);
        setJobError(null);
        setCurrentHistoryId(null);

        toast.success("Đã tạo bài viết thành công!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (jobData.status === "failed") {
        // Job thất bại
        console.log(`❌ Job ${currentJobId} failed:`, jobData.error);

        setJobError(jobData.error || "Có lỗi xảy ra khi tạo bài viết");
        setIsGeneratingPost(false);

        // Cập nhật lịch sử khi thất bại
        if (currentHistoryId) {
          postHistoryService.updateHistory(currentHistoryId, {
            status: "failed",
            errorMessage: jobData.error || "Lỗi không xác định",
          });
          console.log("✅ Đã cập nhật lịch sử thất bại");
        }

        // Reset job states
        setCurrentJobId(null);
        setJobStatus(null);
        setJobProgress(0);
        setCurrentHistoryId(null);

        toast.error(
          `Tạo bài viết thất bại: ${jobData.error || "Lỗi không xác định"}`,
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    });

    // Cleanup listener when component unmounts or job changes
    return () => {
      console.log(`🔇 Stopped listening to job ${currentJobId}`);
      off(jobRef, "value", unsubscribe);
    };
  }, [currentJobId]);

  // Publish Job listener effect
  useEffect(() => {
    if (!currentPublishJobId) return;

    const db = getDatabase();
    const jobRef = ref(db, `jobs/${currentPublishJobId}`);

    console.log(`👂 Listening to publish job ${currentPublishJobId}`);

    const unsubscribe = onValue(jobRef, (snapshot) => {
      const jobData = snapshot.val();

      if (!jobData) {
        console.log(`❌ Publish job ${currentPublishJobId} not found`);
        setPublishJobError("Job không tồn tại");
        return;
      }

      console.log(
        `📊 Publish job ${currentPublishJobId} status:`,
        jobData.status,
        `(${jobData.progress}%)`
      );

      setPublishJobStatus(jobData.status);
      setPublishJobProgress(jobData.progress || 0);

      if (jobData.status === "completed" && jobData.result) {
        // Publish job hoàn thành thành công
        console.log(
          `✅ Publish job ${currentPublishJobId} completed successfully`
        );

        const result = jobData.result;

        // Cập nhật lịch sử khi publish thành công
        const userId = localStorage.getItem("userId");
        if (userId) {
          (async () => {
            try {
              const userHistoryResult = await postHistoryService.getUserHistory(
                userId,
                1
              );
              if (
                userHistoryResult.success &&
                userHistoryResult.data.length > 0
              ) {
                const latestHistory = userHistoryResult.data[0];
                await postHistoryService.updateHistory(latestHistory.id, {
                  wordpressData: {
                    ...latestHistory.wordpressData,
                    published: true,
                    publishedAt: new Date(),
                    seoMetaStatus: result.seoMetaStatus || null,
                    yoastVerified: result.yoastVerified || false,
                    missingYoastChecks: result.missingYoastChecks || [],
                  },
                });
                console.log("✅ Đã cập nhật lịch sử publish thành công");
              }
            } catch (error) {
              console.error("❌ Lỗi khi cập nhật lịch sử publish:", error);
            }
          })();
        }

        // Reset publish job states
        setCurrentPublishJobId(null);
        setPublishJobStatus(null);
        setPublishJobProgress(0);
        setPublishJobError(null);
        setIsPublishing(false);

        toast.success("Đã đăng bài viết thành công!", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (jobData.status === "failed") {
        // Publish job thất bại
        console.log(
          `❌ Publish job ${currentPublishJobId} failed:`,
          jobData.error
        );

        setPublishJobError(jobData.error || "Có lỗi xảy ra khi đăng bài viết");
        setIsPublishing(false);

        // Reset publish job states
        setCurrentPublishJobId(null);
        setPublishJobStatus(null);
        setPublishJobProgress(0);

        toast.error(
          `Đăng bài viết thất bại: ${jobData.error || "Lỗi không xác định"}`,
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    });

    // Cleanup listener when component unmounts or job changes
    return () => {
      console.log(`🔇 Stopped listening to publish job ${currentPublishJobId}`);
      off(jobRef, "value", unsubscribe);
    };
  }, [currentPublishJobId]);

  // Đóng dropdown khi click ra ngoài (website & category)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsWordpressSiteDropdownOpen(false);
      }

      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }

      if (
        styleDropdownRef.current &&
        !styleDropdownRef.current.contains(event.target)
      ) {
        setIsStyleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Keyboard shortcuts for better UX
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ctrl/Cmd + R để mở report modal (chỉ khi có bài viết)
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === "r" &&
        generatedPost
      ) {
        event.preventDefault();
        openReportModal();
      }
      // Escape để đóng modal
      if (event.key === "Escape" && showReportModal) {
        closeReportModal();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [generatedPost, showReportModal]);

  const SEO_CONNECTOR_RECHECK_TTL_MS = 24 * 60 * 60 * 1000; // 24h

  const isSeoConnectorCheckStale = (site) => {
    if (!site || site.seoConnectorInstalled) return false;
    const checkedAt = site.seoConnectorCheckedAt;
    if (!checkedAt) return true;
    const last = Date.parse(checkedAt);
    if (Number.isNaN(last)) return true;
    return Date.now() - last > SEO_CONNECTOR_RECHECK_TTL_MS;
  };

  const recheckSeoConnectorForSite = async (site) => {
    if (!site || !site.id) return;
    if (!site.username || !site.password) return;
    if (!isSeoConnectorCheckStale(site)) return;
    try {
      const result = await checkSeoConnector({
        url: site.url,
        username: site.username,
        password: site.password,
      });
      const installedNow = Boolean(result?.success && result?.data?.installed);
      const userId = localStorage.getItem("userId");
      if (!userId) return;
      const db = getDatabase();
      const siteRef = ref(db, `/users/${userId}/wordpressSites/${site.id}`);
      const patch = {
        seoConnectorInstalled: installedNow,
        seoConnectorCheckedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      if (installedNow && result?.data?.pluginVersion) {
        patch.seoConnectorVersion = result.data.pluginVersion;
      }
      await update(siteRef, patch);
      // Cập nhật state local nếu site đang được chọn để banner biến mất ngay
      setWordpressSites((prev) =>
        prev.map((s) => (s.id === site.id ? { ...s, ...patch } : s))
      );
      setSelectedWordpressSite((prev) =>
        prev && prev.id === site.id ? { ...prev, ...patch } : prev
      );
    } catch (error) {
      // Best-effort: im lặng nếu backend tạm thời không khả dụng
      console.warn("Auto re-check SEO Connector failed for site", site.id, error?.message);
    }
  };

  const loadWordPressSites = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const sitesRef = ref(db, `/users/${userId}/wordpressSites`);
      const snapshot = await get(sitesRef);

      if (snapshot.exists()) {
        const sitesData = snapshot.val();
        const sitesArray = Object.entries(sitesData).map(([id, site]) => ({
          id,
          ...site,
        }));
        setWordpressSites(sitesArray);

        // Auto-select first site if available
        if (sitesArray.length > 0 && !selectedWordpressSite) {
          handleSelectWordpressSite(sitesArray[0]);
        }

        // Auto re-check SEO Connector cho các site còn stale (chưa cài / cache >24h)
        // Không block UI: chạy nền với allSettled, fail im lặng
        Promise.allSettled(sitesArray.map(recheckSeoConnectorForSite)).catch(() => {});
      }
    } catch (error) {
      console.error("Error loading WordPress sites:", error);
    }
  };

  const resetWooProductsState = () => {
    setWooProducts([]);
    setSelectedWooProductIds([]);
    setWooProductsError("");
    setHasLoadedWooProducts(false);
    setWooProductSearchQuery("");
    setWooProductsMeta({
      count: 0,
      total: 0,
      totalPages: 0,
      fetchedAll: false,
      source: "",
    });
  };

  const fetchWooProductsForSite = async (site) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId || !site?.id) return;

      setIsLoadingWooProducts(true);
      setWooProductsError("");

      const { products, meta } = await fetchAllWooProductsForSite(
        site.id,
        userId
      );

      if (Array.isArray(products)) {
        setWooProducts(products);
        setWooProductsMeta(meta);
        setHasLoadedWooProducts(true);

        if (products.length === 0) {
          toast.info("Website này chưa có sản phẩm WooCommerce public.", {
            position: "top-right",
            autoClose: 4000,
          });
        }
      } else {
        setWooProductsError("Không thể tải danh sách sản phẩm WooCommerce.");
        setWooProducts([]);
        setHasLoadedWooProducts(true);
      }
    } catch (error) {
      console.error("Error loading WooCommerce products:", error);
      const message =
        error.response?.data?.message ||
        "Đã xảy ra lỗi khi tải danh sách sản phẩm WooCommerce.";
      setWooProductsError(message);
      setWooProducts([]);
      setWooProductsMeta({
        count: 0,
        total: 0,
        totalPages: 0,
        fetchedAll: false,
        source: "",
      });
      setHasLoadedWooProducts(true);
    } finally {
      setIsLoadingWooProducts(false);
    }
  };

  const handleWooProductToggle = () => {
    const nextValue = !useWooProducts;
    setUseWooProducts(nextValue);
    setIsStyleDropdownOpen(false);

    if (!nextValue) {
      setSelectedWooProductIds([]);
      return;
    }

    // Mặc định luôn bật chế độ chèn link sản phẩm khi vừa bật WooCommerce mode
    setAllowWooProductLinks(true);

    if (selectedWordpressSite && !hasLoadedWooProducts && !isLoadingWooProducts) {
      fetchWooProductsForSite(selectedWordpressSite);
    }
  };

  const handleWooProductSelectionChange = (productId) => {
    setSelectedWooProductIds((prev) => {
      const exists = prev.some((id) => String(id) === String(productId));
      if (exists) {
        return prev.filter((id) => String(id) !== String(productId));
      }

      return [productId];
    });
  };

  const handleSelectWordpressSite = (site) => {
    setSelectedWordpressSite(site);
    const baseUrl = normalizeWordPressSiteUrl(site.url);

    setWordpressConfig({
      url: baseUrl,
      username: site.username,
      password: site.password,
      // categories sẽ được set lại sau khi load từ API categories
      categories: [],
      tags: wordpressConfig.tags || "",
      status: wordpressConfig.status || "publish",
    });
    setIsWordpressSiteDropdownOpen(false);
    resetWooProductsState();
    setUseWooProducts(false);
    setAllowWooProductLinks(true);

    // Fetch theme CSS for accurate preview
    fetchWordPressThemeCSS(baseUrl);

    // Fetch real categories from backend for this site
    fetchWordPressCategoriesForSite(site);
  };

  const fetchWordPressCategoriesForSite = async (site) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId || !site?.id) return;
      if (loadingCategoriesSiteIdRef.current === site.id) return;

      loadingCategoriesSiteIdRef.current = site.id;

      setIsLoadingCategories(true);
      setCategoriesError("");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/wordpress/categories/${site.id}`,
        {
          params: { userId },
        }
      );

      if (response.data?.success) {
        const categories = response.data.data || [];
        setAvailableCategories(categories);

        // Nếu chưa chọn category nào, mặc định chọn category đầu tiên
        if (categories.length > 0) {
          setWordpressConfig((prev) => ({
            ...prev,
            categories:
              prev.categories && prev.categories.length > 0
                ? prev.categories
                : [categories[0].id],
          }));
        }
      } else {
        const message =
          response.data?.message ||
          "Không thể tải danh mục từ website WordPress.";
        setCategoriesError(message);
        toast.error(message, {
          position: "top-right",
          autoClose: 5000,
          toastId: "wordpress-categories-load-error",
        });
      }
    } catch (error) {
      console.error("Error loading WordPress categories:", error);
      const message =
        error.response?.data?.message ||
        "Đã xảy ra lỗi khi tải danh mục từ website WordPress.";
      setCategoriesError(message);
    } finally {
      loadingCategoriesSiteIdRef.current = null;
      setIsLoadingCategories(false);
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    const id = value ? parseInt(value, 10) : null;
    setWordpressConfig((prev) => ({
      ...prev,
      categories: id ? [id] : [],
    }));
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!selectedWordpressSite) {
      toast.error("Vui lòng chọn website WordPress trước khi tạo category!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }
    if (!name) {
      toast.error("Vui lòng nhập tên category mới!", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        toast.error(
          "Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        return;
      }

      setIsCreatingCategory(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/wordpress/categories/${selectedWordpressSite.id}`,
        { name },
        { params: { userId } }
      );

      if (response.data?.success) {
        const category = response.data.data;

        setAvailableCategories((prev) => {
          const exists = prev.some((c) => c.id === category.id);
          if (exists) return prev;
          return [...prev, category];
        });

        setWordpressConfig((prev) => ({
          ...prev,
          categories: [category.id],
        }));

        toast.success(`Đã tạo category mới: ${category.name}`, {
          position: "top-right",
          autoClose: 4000,
        });

        setNewCategoryName("");
      } else {
        const message =
          response.data?.message ||
          "Không thể tạo category mới trên WordPress.";
        // toast.error(message, {
        //   position: "top-right",
        //   autoClose: 5000,
        // });
      }
    } catch (error) {
      console.error("Error creating WordPress category:", error);
      const message =
        error.response?.data?.message ||
        "Đã xảy ra lỗi khi tạo category mới trên WordPress.";
      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
  };

  // Premium Grounding: opt-in toggle. Default OFF.
  // GroundingToggle component passes the new value (bool), not an event.
  const handleUseGoogleSearchChange = (newValue) => {
    setUseGoogleSearch(newValue);
  };

  const checkPermission = () => {
    // Chỉ cho phép gói Cao cấp (membership === 3), VIP (membership === 4) hoặc dùng thử Premium (membership === 6) sử dụng tính năng này
    if (userMembership !== 3 && userMembership !== 4 && userMembership !== 6) {
      toast.error(
        "Tính năng tạo bài viết website chỉ dành cho gói Cao cấp và VIP. Vui lòng nâng cấp gói để sử dụng!",
        {
          position: "top-right",
          autoClose: 6000,
        }
      );
      return false;
    }

    return true;
  };

  const checkPointsForGenerating = () => {
    // Nếu có lượt miễn phí (chỉ áp dụng cho gói Trải nghiệm), không cần kiểm tra điểm
    if (hasFreeWebsiteQuota) {
      return true;
    }

    // Nếu hết lượt miễn phí, kiểm tra điểm để tạo bài viết
    if (userPoint < requiredWebsitePostPoints) {
      toast.error(
        `Bạn đã hết lượt miễn phí và không đủ điểm để tạo bài viết. Cần ${requiredWebsitePostPoints} điểm, hiện có ${userPoint} điểm!`,
        {
          position: "top-right",
          autoClose: 7000,
        }
      );
      return false;
    }
    return true;
  };

  const checkPointsForPublishing = () => {
    // Chỉ kiểm tra điểm khi đăng bài viết (không trừ điểm ở đây nữa)
    // Logic trừ điểm đã được chuyển về generatePost
    return true;
  };

  const generatePost = async () => {
    // Kiểm tra nếu người dùng đã chỉnh sửa bài viết
    if (generatedPost && hasEdited) {
      if (
        !window.confirm(
          "Bạn đã chỉnh sửa bài viết. Tạo bài viết mới sẽ xóa các chỉnh sửa của bạn. Bạn có chắc chắn muốn tiếp tục?"
        )
      ) {
        return;
      }
    }

    if (!checkPermission()) return;

    // Kiểm tra điểm trước khi tạo bài viết
    if (!checkPointsForGenerating()) return;

    // Validate URLs trước khi tạo bài viết
    const urlValidation = validateUrls(inputUrls);
    if (!urlValidation.isValid) {
      setUrlValidationErrors(urlValidation.errors);
      toast.error("Vui lòng sửa lỗi URL trước khi tạo bài viết!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const selectedWooProducts = getSelectedWooProducts(
      wooProducts,
      selectedWooProductIds
    );
    const mergedReferenceUrls = mergeReferenceUrls(
      urlValidation.urls || [],
      useWooProducts && allowWooProductLinks ? selectedWooProducts : []
    );
    const isUploadImageSourceSelected =
      !useWooProducts && imageSource === IMAGE_SOURCES.UPLOAD;
    const activeUserImages = isUploadImageSourceSelected ? userImages : [];

    if (requiresWooProductSelection(useWooProducts, selectedWooProductIds)) {
      toast.error(
        "Bạn đã bật chế độ sản phẩm WooCommerce nhưng chưa chọn sản phẩm nào.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    // Kiểm tra phải có ít nhất requirements hoặc URL hoặc ảnh
    const hasRequirements = requirements && requirements.trim();
    const hasUrls = mergedReferenceUrls.length > 0;
    const hasWooProducts = selectedWooProducts.length > 0;
    const hasImages = activeUserImages && activeUserImages.length > 0;

    if (isUploadImageSourceSelected && !hasImages) {
      toast.error(
        "Bạn đã chọn nguồn ảnh tự cung cấp nhưng chưa thêm ảnh nào.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    if (!hasRequirements && !hasUrls && !hasWooProducts && !hasImages) {
      toast.error(
        "Vui lòng nhập yêu cầu, URL tham khảo, chọn sản phẩm WooCommerce HOẶC upload ảnh để tạo bài viết!",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    // selectedWordpressSite giờ là tùy chọn — khi không chọn website,
    // người dùng có thể tạo bài viết để copy đăng thủ công lên website khác.

    // Validation cho thông tin website
    if (useWebsiteInfo && selectedWordpressSite && !selectedWordpressSite.websiteInfo) {
      toast.warning(
        "Website được chọn không có thông tin mô tả. Bài viết sẽ được tạo mà không có thông tin website.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }

    // Reset job states và bắt đầu generating
    setCurrentJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobError(null);
    setIsGeneratingPost(true);

    const userId = localStorage.getItem("userId");

    // Tạo lịch sử ngay khi bắt đầu
    const historyData = {
      userId,
      postData: {
        requirements,
        language: selectedLanguage,
        postLength,
        postStyles,
        imageSource,
        imageStyle,
        aspectRatio,
        customImagePrompt,
        allowNoImage,
        imageValidationEnabled,
        requiredPoints: requiredWebsitePostPoints,
        urls: mergedReferenceUrls,
        useWooProducts,
        allowProductLinks: useWooProducts && allowWooProductLinks,
        products: selectedWooProducts,
        useWebsiteInfo,
        websiteInfo: useWebsiteInfo
          ? (selectedWordpressSite?.websiteInfo || "")
          : "",
        includeContactInfo,
      },
      wordpressData: selectedWordpressSite
        ? {
            siteId: selectedWordpressSite.id,
            siteName: selectedWordpressSite.siteName,
            siteUrl: selectedWordpressSite.url,
            useWooProducts,
            allowProductLinks: useWooProducts && allowWooProductLinks,
            productIds: selectedWooProducts.map((product) => product.id),
            productNames: selectedWooProducts.map((product) => product.name),
            status: wordpressConfig.status,
            categories: wordpressConfig.categories,
            tags: wordpressConfig.tags,
            published: false,
          }
        : {
            siteId: null,
            siteName: "Không chọn website",
            siteUrl: null,
            useWooProducts: false,
            allowProductLinks: false,
            productIds: [],
            productNames: [],
            status: "draft",
            categories: [],
            tags: "",
            published: false,
          },
      pointsData: {
        pointsDeducted: false,
        pointsDeductedAmount: hasFreeWebsiteQuota ? 0 : requiredWebsitePostPoints,
        pointsRefunded: false,
        pointsRefundedAmount: 0,
        usedFreeQuantity: hasFreeWebsiteQuota,
        userMembership,
      },
      status: "processing",
      jobId: null,
    };

    // Lưu lịch sử với enhanced error handling
    console.log("📝 Bắt đầu tạo lịch sử bài viết...");
    console.log("📋 History data:", historyData);

    let historyResult;
    try {
      historyResult = await postHistoryService.createHistory(historyData);
      console.log("📤 postHistoryService.createHistory result:", historyResult);

      if (historyResult.success) {
        setCurrentHistoryId(historyResult.id);
        console.log("✅ Đã tạo lịch sử bài viết thành công:", historyResult.id);
      } else {
        console.error("❌ Lỗi khi tạo lịch sử:", historyResult.error);
        console.error("❌ Full error object:", historyResult);
      }
    } catch (error) {
      console.error("❌ Exception khi tạo lịch sử:", error);
      console.error("❌ Error stack:", error.stack);
      historyResult = { success: false, error: error.message };
    }

    // Debug logging
    console.log("useWebsiteInfo:", useWebsiteInfo);
    console.log("selectedWordpressSite:", selectedWordpressSite);
    console.log(
      "websiteInfo being sent:",
      useWebsiteInfo ? (selectedWordpressSite?.websiteInfo || "") : ""
    );

    try {
      // Chuẩn bị user images để gửi
      const userImagesForUpload = activeUserImages.map((img) => ({
        type: img.type, // 'url' hoặc 'file'
        data: img.data, // base64 data URL hoặc URL
      }));

      const requestData = {
        options: {
          prompt: requirements,
          postLength: postLength,
          postStyles: postStyles.join(", "),
          language: selectedLanguage,
          websiteInfo: useWebsiteInfo
            ? (selectedWordpressSite?.websiteInfo || "")
            : "",
          useWebsiteInfo: useWebsiteInfo, // Thêm flag này để backend biết
          includeContactInfo, // Thêm flag này để backend append contactInfo vào cuối bài
          urls: mergedReferenceUrls, // Merge URLs nhập tay + product permalinks
          allowProductLinks: useWooProducts && allowWooProductLinks,
          products: selectedWooProducts,
          userImages: userImagesForUpload, // Thêm ảnh từ người dùng
          imageSource,
          wpImageCount:
            imageSource === IMAGE_SOURCES.UPLOAD
              ? userImagesForUpload.length
              : wpImageCount,
          imageStyle,
          aspectRatio,
          customImagePrompt,
          allowNoImage,
          imageValidationEnabled,
          requiredPoints: requiredWebsitePostPoints,
          contentFormat,
          enableGeoOptimization: true,
        },
        wordpressConfig: selectedWordpressSite
          ? {
              url: wordpressConfig.url,
              username: wordpressConfig.username,
              password: wordpressConfig.password,
              siteId: selectedWordpressSite.id,
              siteName: selectedWordpressSite.siteName,
            }
          : null, // Không chọn website → tạo nội dung để copy thủ công
        userId,
      };

      console.log("Request data being sent:", {
        ...requestData,
        wordpressConfig: requestData.wordpressConfig
          ? {
              ...requestData.wordpressConfig,
              password: requestData.wordpressConfig.password ? "***" : "",
            }
          : requestData.wordpressConfig,
        options: {
          ...requestData.options,
          userImages: `${userImagesForUpload.length} image(s)`,
        },
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/content/generate-wordpress-post`,
        requestData
      );

      if (response.data.success) {
        console.log("✅ Job created successfully:", response.data);

        const { jobId, message, estimatedTime } = response.data.data;

        // Cập nhật lịch sử với jobId
        if (currentHistoryId) {
          await postHistoryService.updateHistory(currentHistoryId, {
            jobId,
            status: "processing",
          });
          console.log("✅ Đã cập nhật lịch sử với jobId:", jobId);
        }

        // Set job ID để bắt đầu lắng nghe
        setCurrentJobId(jobId);
        setJobStatus("pending");
        setJobProgress(0);

        toast.info(`${message} (Ước tính: ${estimatedTime})`, {
          position: "top-right",
          autoClose: 4000,
        });

        // Payment được xử lý hoàn toàn bởi backend thông qua handleWebsitePostPayment()
        console.log("✅ Payment sẽ được xử lý bởi backend khi job hoàn thành");
      } else {
        // Tạo job thất bại
        setIsGeneratingPost(false);
        toast.error(
          response.data.message ||
            "Không thể tạo job xử lý bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo job:", error);
      setIsGeneratingPost(false);

      // Xử lý các loại lỗi từ backend validation
      if (
        error.response?.status === 402 &&
        error.response?.data?.code === "INSUFFICIENT_PAYMENT"
      ) {
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 7000,
        });
      } else if (
        error.response?.status === 403 &&
        error.response?.data?.code === "INSUFFICIENT_PERMISSION"
      ) {
        toast.error(error.response.data.message, {
          position: "top-right",
          autoClose: 7000,
        });
      } else if (
        error.response?.status === 404 &&
        error.response?.data?.code === "USER_NOT_FOUND"
      ) {
        toast.error(
          "Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else if (
        error.code === "ECONNABORTED" ||
        error.message.includes("timeout")
      ) {
        // Xử lý lỗi timeout đặc biệt
        toast.error(
          "Yêu cầu đang được xử lý ở server. Vui lòng chờ trong giây lát...",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        // Lỗi chung
        const errorMessage =
          error.response?.data?.message ||
          "Đã xảy ra lỗi khi tạo bài viết. Vui lòng thử lại sau.";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      if (editedPostText !== generatedPost.text) {
        setGeneratedPost((prevPost) => ({
          ...prevPost,
          text: editedPostText,
        }));
        setHasEdited(true);
        toast.success("Đã lưu thay đổi nội dung bài viết!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } else {
      setEditedPostText(generatedPost.text);
    }
    setIsEditMode(!isEditMode);
  };

  const cancelEdit = () => {
    setEditedPostText(generatedPost.text);
    setIsEditMode(false);
  };

  const copyToClipboard = () => {
    if (!generatedPost) return;

    navigator.clipboard
      .writeText(generatedPost.text)
      .then(() => {
        toast.success("Đã sao chép nội dung bài viết vào clipboard!", {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .catch((error) => {
        console.error("Lỗi khi sao chép vào clipboard:", error);
        toast.error("Không thể sao chép nội dung. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  const publishPost = async () => {
    if (!generatedPost || !selectedWordpressSite) {
      toast.error("Không có bài viết để đăng hoặc chưa chọn website!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Kiểm tra điểm trước khi đăng bài viết
    if (!checkPointsForPublishing()) {
      return;
    }

    // Reset publish job states và bắt đầu publishing
    setCurrentPublishJobId(null);
    setPublishJobStatus(null);
    setPublishJobProgress(0);
    setPublishJobError(null);

    setIsPublishing(true);

    const userId = localStorage.getItem("userId");
    const publishImages =
      hasAutoworkImagePlaceholders(generatedPost.text) ||
      !hasInlinePostImages(generatedPost.text)
        ? normalizeGeneratedImageSources(generatedPost.images)
        : [];

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/content/publish-wordpress-post`,
        {
          post: {
            title: generatedPost.title,
            content: generatedPost.text,
            metaDescription: generatedPost.metaDescription, // ✅ Truyền meta description từ AI (155-158 ký tự)
            focusKeyphrase: generatedPost.focusKeyphrase, // ✅ Truyền focus keyphrase từ AI cho Yoast SEO
            seoTitle: generatedPost.seoTitle,
            slug: generatedPost.slug,
            schemaJsonLd: generatedPost.schemaJsonLd,
            seoTags: generatedPost.seoTags || [], // ✅ Truyền SEO tags từ AI để tạo/gán tags trên WordPress
            internalLinkCandidates:
              generatedPost.internalLinkCandidates ||
              (useWooProducts && allowWooProductLinks
                ? selectedWooProducts
                    .map((product) => ({
                      url: product.permalink || product.url || product.link,
                      title: product.name || product.title,
                    }))
                    .filter((candidate) => candidate.url && candidate.title)
                : []),
            images: publishImages, // ✅ Truyền ảnh AI/nguồn ảnh đã tạo để WordPress upload và set featured image
            includeContactInfo, // ✅ Truyền flag để BE tự chèn contactInfo của website vào cuối bài
          },
          useAICategory: categoryMode === "ai",
          // skipCategory: true nếu disabled HOẶC nếu manual nhưng không có categories nào được chọn (do không load được hoặc chưa chọn)
          skipCategory:
            categoryMode === "disabled" ||
            (categoryMode === "manual" &&
              (!wordpressConfig.categories ||
                wordpressConfig.categories.length === 0)),
          wordpressConfig: {
            siteId: selectedWordpressSite?.id || wordpressConfig.siteId,
            siteName: selectedWordpressSite?.siteName || wordpressConfig.siteName,
            url: wordpressConfig.url,
            username: wordpressConfig.username,
            password: wordpressConfig.password,
            // Tắt danh mục hoặc manual không có categories -> gửi []. AI mode -> gửi []. Manual có categories -> gửi categories đã chọn.
            categories:
              categoryMode === "disabled"
                ? []
                : categoryMode === "ai"
                ? []
                : wordpressConfig.categories &&
                  wordpressConfig.categories.length > 0
                ? wordpressConfig.categories
                : [],
            tags: wordpressConfig.tags,
            status: wordpressConfig.status,
          },
          userId,
          postMetadata: generatedPost.metadata || {
            pointsDeducted: true,
            pointsDeductedAmount: requiredWebsitePostPoints,
            pointsRefunded: false,
          },
        }
      );

      if (response.data.success) {
        console.log("✅ Publish job created successfully:", response.data);

        const { jobId, message, estimatedTime } = response.data.data;

        // Set publish job ID để bắt đầu lắng nghe
        setCurrentPublishJobId(jobId);
        setPublishJobStatus("pending");
        setPublishJobProgress(0);

        toast.info(`${message} (Ước tính: ${estimatedTime})`, {
          position: "top-right",
          autoClose: 4000,
        });
      } else {
        // Tạo publish job thất bại
        setIsPublishing(false);
        toast.error(
          response.data.message ||
            "Không thể tạo job đăng bài viết. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo publish job:", error);
      setIsPublishing(false);

      // Xử lý lỗi timeout đặc biệt
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        toast.error(
          "Yêu cầu đăng bài đang được xử lý ở server. Vui lòng chờ trong giây lát...",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        toast.error("Đã xảy ra lỗi khi đăng bài viết. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };



  const handleWordpressStatusChange = (status) => {
    setWordpressConfig((prev) => ({
      ...prev,
      status: status,
    }));
  };

  const checkPoints = () => {
    if (userPoint <= 0) {
      toast.error(
        "Bạn đã hết điểm! Vui lòng nạp thêm điểm để tiếp tục sử dụng dịch vụ.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return false;
    }
    return true;
  };

  const suggestTopics = () => {
    // REMOVED: Không kiểm tra điểm cho gợi ý chủ đề

    let info = "những chủ đề đang hot trên mạng xã hội facebook";

    if (requirements) {
      info = requirements;
    }

    if (info.trim().length > 8000) {
      toast.error(
        "Thông tin gợi ý chủ đề không được vượt quá 8000 ký tự. Vui lòng rút ngắn nội dung!",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    setIsSuggestingTopics(true);
    apiClient
      .post("/api/content/suggest-topics", {
        userId: localStorage.getItem("userId"),
        source: "website-post",
        info,
        language: selectedLanguage,
        topicCount: 10,
      })
      .then((response) => {
        const { data } = response;
        setSuggestedTopics(Array.isArray(data.topics) ? data.topics : []);
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          error?.response?.data?.message ||
            "Không thể gợi ý chủ đề. Vui lòng thử lại sau.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      })
      .finally(() => {
        setIsSuggestingTopics(false);
      });
  };

  const clearSuggestedTopics = () => {
    setSuggestedTopics([]);
    setSelectedTopic("");
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setRequirements(topic);
  };

  // Handler for website information usage toggle
  const handleUseWebsiteInfoChange = () => {
    setUseWebsiteInfo(!useWebsiteInfo);
  };

  // Handler for contact info append toggle
  const handleIncludeContactInfoChange = () => {
    setIncludeContactInfo(!includeContactInfo);
  };

  const handleImageSourceChange = (source) => {
    if (source === IMAGE_SOURCES.AI && userMembership < 3) {
      return;
    }

    if (source !== IMAGE_SOURCES.AI) {
      setIsStyleDropdownOpen(false);
    }

    setImageSource(source);
    setAllowNoImage(source === IMAGE_SOURCES.NONE);
    setAspectRatio(
      resolveWebsiteAspectRatio({
        aspectRatio,
        imageSource: source,
      })
    );
  };

  const handleImageStyleChange = (style) => {
    setImageStyle(style);
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
  };

  const handleCustomImagePromptChange = (event) => {
    setCustomImagePrompt(event.target.value);
  };

  const handleImageValidationChange = () => {
    setImageValidationEnabled((prev) => !prev);
  };

  useEffect(() => {
    if (
      imageSource === IMAGE_SOURCES.AI &&
      (aspectRatio === "16:9" ||
        aspectRatio === DEFAULT_ASPECT_RATIO ||
        !aspectRatio)
    ) {
      setAspectRatio(WEBSITE_TOPIC_ASPECT_RATIO);
    }
  }, [imageSource, aspectRatio]);

  // Report handling functions
  const openReportModal = () => {
    setShowReportModal(true);
    setReportType("");
    setReportDescription("");
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportType("");
    setReportDescription("");
  };

  const handleMetaDescriptionGuide = () => {
    window.open(
      "https://youtu.be/bw7zG8mOe-g",
      "_blank",
      "noopener,noreferrer"
    );
    toast.success("Đã mở video hướng dẫn thiết lập Meta Description!", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const handleReportSubmit = async () => {
    if (!reportType) {
      toast.error("Vui lòng chọn loại lỗi!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmittingReport(true);
    const userId = localStorage.getItem("userId");

    try {
      const reportData = {
        userId: userId || "Người dùng ẩn danh",
        errorType: reportType,
        description: reportDescription || "Không có mô tả chi tiết",
        language: getLanguageName(selectedLanguage),
        postLength: postLength,
        postStyles: postStyles.join(", "),
        referenceUrl: inputUrls || null,
        postTitle: generatedPost?.title || "Không có tiêu đề",
        postContent: generatedPost?.text || "Không có nội dung",
        userAgent: navigator.userAgent,
        websiteInfo: useWebsiteInfo ? selectedWordpressSite?.websiteInfo : null,
        siteName: selectedWordpressSite?.siteName || "Không xác định",
      };

      const success = await sendPostReportNotification(reportData);

      if (success) {
        toast.success(
          "🙏 Cảm ơn bạn đã báo cáo! Chúng tôi sẽ cải thiện hệ thống.",
          {
            position: "top-right",
            autoClose: 4000,
          }
        );
        closeReportModal();
      } else {
        toast.error("Không thể gửi báo cáo. Vui lòng thử lại sau.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Lỗi khi gửi báo cáo:", error);
      toast.error("Đã xảy ra lỗi khi gửi báo cáo. Vui lòng thử lại sau.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Pre-defined report types for easier reporting
  const reportTypes = [
    { value: "content_quality", label: "🤖 Nội dung AI không tự nhiên, kỳ lạ" },
    {
      value: "content_incomplete",
      label: "📝 Nội dung thiếu sót, không đầy đủ",
    },
    {
      value: "content_irrelevant",
      label: "🎯 Nội dung không liên quan đến yêu cầu",
    },
    { value: "image_error", label: "🖼️ Ảnh lỗi hoặc không phù hợp" },
    { value: "formatting_error", label: "📋 Định dạng HTML/WordPress bị lỗi" },
    { value: "language_error", label: "🌐 Ngôn ngữ sai hoặc lỗi ngữ pháp" },
    { value: "duplicate_content", label: "📄 Nội dung bị lặp lại" },
    { value: "url_processing_error", label: "🔗 Lỗi xử lý URL tham khảo" },
    { value: "other", label: "❓ Lỗi khác" },
  ];

  // URL validation function (từ NormalPostGenerator.js)
  const validateUrls = (urlString) => {
    if (!urlString.trim()) {
      return { isValid: true, urls: [], errors: [] };
    }

    // Chỉ lấy URL đầu tiên từ input
    const firstUrl = urlString
      .trim()
      .split(/[,\n\s]+/)[0]
      .trim();

    const validUrls = [];
    const errors = [];

    if (firstUrl) {
      try {
        // Thêm https:// nếu URL không có protocol
        let normalizedUrl = firstUrl;
        if (!firstUrl.match(/^https?:\/\//)) {
          normalizedUrl = `https://${firstUrl}`;
        }

        const urlObj = new URL(normalizedUrl);

        // Kiểm tra xem có phải là URL hợp lệ không
        if (urlObj.protocol === "http:" || urlObj.protocol === "https:") {
          validUrls.push(normalizedUrl);
        } else {
          errors.push(`Protocol không được hỗ trợ (${urlObj.protocol})`);
        }
      } catch (error) {
        errors.push(`"${firstUrl}" không phải là URL hợp lệ`);
      }
    }

    // Kiểm tra nếu người dùng nhập nhiều URL
    const allUrls = urlString
      .trim()
      .split(/[,\n\s]+/)
      .filter((url) => url.trim().length > 0);
    if (allUrls.length > 1) {
      errors.push("Chỉ hỗ trợ 1 URL duy nhất");
    }

    return {
      isValid: errors.length === 0 && validUrls.length <= 1,
      urls: validUrls,
      errors: errors,
    };
  };

  // Hàm xử lý thay đổi URL input
  const handleUrlsChange = (value) => {
    setInputUrls(value);

    // Reset errors khi người dùng đang gõ
    if (urlValidationErrors.length > 0) {
      setUrlValidationErrors([]);
    }
  };

  // Hàm validate URLs khi người dùng blur
  const handleUrlsBlur = () => {
    const validation = validateUrls(inputUrls);
    setUrlValidationErrors(validation.errors);
  };

  // ===== HANDLERS FOR USER IMAGES =====

  // Hàm xử lý khi người dùng upload file ảnh
  const handleImageFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Kiểm tra số lượng ảnh sẽ vượt quá giới hạn
    const availableSlots = MAX_USER_IMAGES - userImages.length;
    if (availableSlots <= 0) {
      toast.error(`Tối đa ${MAX_USER_IMAGES} ảnh. Vui lòng xóa ảnh cũ để thêm ảnh mới.`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Giới hạn số file được xử lý dựa trên số slot còn trống
    const filesToProcess = Array.from(files).slice(0, availableSlots);

    if (files.length > availableSlots) {
      toast.warning(
        `Chỉ có thể thêm ${availableSlots} ảnh. ${
          files.length - availableSlots
        } ảnh bị bỏ qua.`,
        {
          position: "top-right",
          autoClose: 4000,
        }
      );
    }

    setIsCompressingImage(true);

    const newImages = [];
    let successCount = 0;
    let errorMessages = [];

    // Xử lý từng file
    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];

      try {
        console.log(
          `📸 Bắt đầu nén ảnh ${i + 1}/${filesToProcess.length}:`,
          file.name
        );

        // Nén xuống 1MB để đảm bảo tổng payload (2 ảnh + text) < 5MB (giới hạn Vercel)
        // 1MB binary ~ 1.33MB base64. 2 ảnh ~ 2.66MB base64 -> An toàn.
        const result = await compressImage(file, 1.0);

        console.log(`✅ Ảnh ${file.name} đã nén thành công:`, {
          original: formatFileSize(result.originalSize),
          compressed: formatFileSize(result.compressedSize),
          ratio:
            ((result.compressedSize / result.originalSize) * 100).toFixed(1) +
            "%",
        });

        // Tạo object ảnh mới với ID duy nhất
        const newImage = {
          id: Date.now() + i, // Đảm bảo ID khác nhau
          type: "file",
          data: result.dataUrl,
          preview: result.dataUrl,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
          fileName: file.name,
        };

        newImages.push(newImage);
        successCount++;
      } catch (error) {
        console.error(`❌ Lỗi nén ảnh ${file.name}:`, error);
        errorMessages.push(`${file.name}: ${error.message}`);
      }
    }

    // Cập nhật state với tất cả ảnh mới
    if (newImages.length > 0) {
      setUserImages((prevImages) => [...prevImages, ...newImages]);

      if (successCount === 1) {
        toast.success(`Đã thêm ảnh: ${newImages[0].fileName}`, {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        toast.success(`Đã thêm ${successCount} ảnh thành công`, {
          position: "top-right",
          autoClose: 2000,
        });
      }
    }

    // Hiển thị lỗi nếu có
    if (errorMessages.length > 0) {
      toast.error(
        `Có ${errorMessages.length} ảnh bị lỗi: ${errorMessages.join(", ")}`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }

    setIsCompressingImage(false);
    // Reset file input
    event.target.value = "";
  };

  // Hàm xử lý thêm URL ảnh
  const handleAddImageUrl = async () => {
    if (!imageInputUrl.trim()) {
      setImageInputErrors(["Vui lòng nhập URL ảnh"]);
      return;
    }

    // Kiểm tra số lượng ảnh
    if (userImages.length >= MAX_USER_IMAGES) {
      setImageInputErrors([`Tối đa ${MAX_USER_IMAGES} ảnh`]);
      return;
    }

    try {
      setIsCompressingImage(true);
      console.log("🔗 Kiểm tra URL ảnh:", imageInputUrl);

      // Validate URL
      await validateImageUrl(imageInputUrl);

      // Thêm ảnh từ URL
      const newImage = {
        id: Date.now(),
        type: "url",
        data: imageInputUrl,
        preview: imageInputUrl,
      };

      setUserImages([...userImages, newImage]);
      setImageInputUrl("");
      setImageInputErrors([]);

      toast.success("Đã thêm ảnh từ URL", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("❌ Lỗi URL ảnh:", error);
      setImageInputErrors([error.message]);
    } finally {
      setIsCompressingImage(false);
    }
  };

  // Hàm xóa ảnh
  const handleRemoveImage = (imageId) => {
    setUserImages(userImages.filter((img) => img.id !== imageId));
    toast.info("Đã xóa ảnh", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  // @DEPRECATED: Hàm này không còn được sử dụng - logic ảnh đã chuyển về backend
  // Hàm tìm kiếm ảnh liên quan đến nội dung bài viết
  const fetchRelatedImages = async (
    content,
    title,
    hasUrlsParam,
    urlValidationParam
  ) => {
    try {
      setIsLoadingImages(true);
      const userId = localStorage.getItem("userId");

      // Tạo query tốt hơn cho URL context
      let query;
      if (
        hasUrlsParam &&
        urlValidationParam.urls &&
        urlValidationParam.urls.length > 0
      ) {
        // Nếu có URL context, sử dụng title hoặc extract keywords từ content
        if (
          title &&
          title !== "Bài viết WordPress" &&
          !title.includes("WordPress")
        ) {
          query = title;
        } else {
          // Extract keywords từ content nếu title không phù hợp
          const contentText = content
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          const keywords = contentText.split(" ").slice(0, 5).join(" ");
          query = keywords || content.substring(0, 100);
        }
        console.log(`🔍 Using URL context query: "${query}"`);
      } else {
        query = title || content.substring(0, 100);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/content/get-images-hybrid`,
        {
          query: query,
          userId,
          limit: 2, // Lấy 2 ảnh để đảm bảo consistency
        }
      );

      if (response.data.success) {
        const images = response.data.data.images || [];
        console.log(`🖼️ Found ${images.length} related images`);
        return images;
      }
      return [];
    } catch (error) {
      console.error("Error fetching related images:", error);
      return [];
    } finally {
      setIsLoadingImages(false);
    }
  };

  // @DEPRECATED: Hàm này không còn được sử dụng - logic ảnh đã chuyển về backend
  // Hàm chèn ảnh vào nội dung bài viết
  const insertImagesIntoContent = (content, images) => {
    if (!images || images.length === 0) return content;

    // Tách nội dung thành các đoạn
    const paragraphs = content.split("\n\n");

    // Tính toán vị trí chèn ảnh
    const imagePositions = [];
    const totalParagraphs = paragraphs.length;

    if (totalParagraphs >= 3) {
      // Chèn ảnh đầu tiên sau đoạn thứ 2
      imagePositions.push(Math.floor(totalParagraphs * 0.3));

      if (images.length > 1 && totalParagraphs >= 5) {
        // Chèn ảnh thứ hai ở giữa
        imagePositions.push(Math.floor(totalParagraphs * 0.6));
      }

      if (images.length > 2 && totalParagraphs >= 7) {
        // Chèn ảnh thứ ba gần cuối
        imagePositions.push(Math.floor(totalParagraphs * 0.8));
      }
    }

    // Chèn ảnh vào các vị trí đã tính toán
    let result = [];
    for (let i = 0; i < paragraphs.length; i++) {
      result.push(paragraphs[i]);

      // Kiểm tra xem có cần chèn ảnh ở vị trí này không
      const imageIndex = imagePositions.indexOf(i);
      if (imageIndex !== -1 && images[imageIndex]) {
        const imageHtml = `\n\n<figure class="wp-block-image size-large"><img src="${images[imageIndex].imageUrl}" alt="" class="wp-image" /></figure>\n\n`;
        result.push(imageHtml);
      }
    }

    return result.join("\n\n");
  };

  const selectedWooProducts = getSelectedWooProducts(
    wooProducts,
    selectedWooProductIds
  );
  const totalWooProducts = wooProductsMeta.total || wooProducts.length;
  const filteredWooProducts = useMemo(() => {
    const q = wooProductSearchQuery.trim().toLowerCase();
    if (!q) return wooProducts;
    return wooProducts.filter((product) => {
      const name = (product.name || "").toLowerCase();
      const shortDesc = (product.shortDescription || "").toLowerCase();
      const link = (product.permalink || "").toLowerCase();
      const price = (product.priceText || "").toLowerCase();
      return (
        name.includes(q) ||
        shortDesc.includes(q) ||
        link.includes(q) ||
        price.includes(q)
      );
    });
  }, [wooProducts, wooProductSearchQuery]);
  const shouldShowImageSourceSection = !useWooProducts;
  const isUploadSourceActive =
    shouldShowImageSourceSection && imageSource === IMAGE_SOURCES.UPLOAD;
  const isUploadSourceMissingImages =
    isUploadSourceActive && userImages.length === 0;
  const aspectRatioOptions = WEBSITE_TOPIC_ASPECT_RATIO_OPTIONS;
  const urlImageHint =
    isUploadSourceActive && userImages.length > 0
      ? "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn. Ảnh bạn tự thêm sẽ được ưu tiên sử dụng."
      : isUploadSourceActive
      ? "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn. Bạn cần thêm ít nhất 1 ảnh tự cung cấp để tạo bài viết."
      : useWooProducts
      ? "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn và ưu tiên ảnh từ sản phẩm WooCommerce đã chọn."
      : imageSource === IMAGE_SOURCES.AI
      ? "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn và tự tạo ảnh AI đồng bộ với nội dung."
      : imageSource === IMAGE_SOURCES.NONE
      ? "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn và xuất bài viết không kèm ảnh tự động."
      : "AI sẽ tham khảo nội dung từ URL để tạo bài viết phù hợp hơn và tự động tìm ảnh liên quan.";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
          TẠO BÀI VIẾT WEBSITE VỚI AI
        </h1>

        {/* Video hướng dẫn Meta Description */}
        <div className="flex justify-center mb-4 sm:mb-6 md:mb-8">
          <button
            onClick={handleMetaDescriptionGuide}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transform transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
            title="Xem video hướng dẫn thiết lập thẻ Meta Description cho bài viết"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            <span>Hướng dẫn thiết lập Meta Description</span>
          </button>
        </div>

        <PointWarning
          title="Vui lòng nạp thêm điểm để tiếp tục dùng chức năng này nhé ;)"
          userPoint={userPoint}
        />

        {/* Thông tin về quyền truy cập và lượt tạo bài viết */}
        {userMembership === 3 || userMembership === 4 || userMembership === 6 ? (
          <>
            {hasFreeWebsiteQuota && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-blue-400 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      🎁 Bạn còn {websitePostQuantity} lượt tạo bài viết MIỄN
                      PHÍ
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Sau khi hết lượt miễn phí, bài viết hiện tại sẽ tính{" "}
                      {requiredWebsitePostPoints} điểm (hiện có {userPoint}{" "}
                      điểm)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-red-400 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm text-red-800 font-medium">
                  🚫 Tính năng chỉ dành cho gói Cao cấp và VIP
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Vui lòng nâng cấp gói để sử dụng tính năng tạo bài viết
                  website
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Thông báo cho người dùng gói cao cấp khi hết lượt miễn phí và không đủ điểm */}
        {(userMembership === 3 || userMembership === 4 || userMembership === 6) &&
          (userMembership === 6 ? websitePostQuantity === 0 : true) &&
          userPoint < requiredWebsitePostPoints && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg
                  className="h-5 w-5 text-orange-400 mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm text-orange-800 font-medium">
                    ⚠️ Cần nạp thêm điểm để tiếp tục tạo bài viết
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    Đã hết lượt miễn phí và không đủ điểm (cần{" "}
                    {requiredWebsitePostPoints} điểm, hiện có {userPoint} điểm)
                  </p>
                </div>
              </div>
            </div>
          )}

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần bên trái - Cấu hình */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-md shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">
                Cấu hình bài viết
              </h2>

              {/* Ngôn ngữ */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Ngôn ngữ bài viết
                </label>
                <PostLanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                />
              </div>

              {/* WordPress Site Selection */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Website WordPress{" "}
                  <span className="text-sm font-normal text-gray-400">(tùy chọn)</span>
                </label>
                {wordpressSites.length > 0 ? (
                  <div className="relative z-20" ref={dropdownRef}>
                    <button
                      onClick={() =>
                        setIsWordpressSiteDropdownOpen(
                          !isWordpressSiteDropdownOpen
                        )
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between"
                    >
                      <span className="truncate">
                        {selectedWordpressSite
                          ? selectedWordpressSite.siteName
                          : "✈️ Không chọn website — tạo bài để copy"}
                      </span>
                      <FiRefreshCw
                        className={`transform transition-transform ${
                          isWordpressSiteDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isWordpressSiteDropdownOpen && (
                      <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {/* Option để bỏ chọn website */}
                        <button
                          onClick={() => {
                            setSelectedWordpressSite(null);
                            setIsWordpressSiteDropdownOpen(false);
                            setUseWebsiteInfo(false);
                            resetWooProductsState();
                            setUseWooProducts(false);
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-200 ${
                            !selectedWordpressSite ? "bg-blue-50" : ""
                          }`}
                        >
                          <div className="font-medium text-gray-600">✈️ Không chọn website</div>
                          <div className="text-sm text-gray-400">
                            Tạo bài viết để copy đăng thủ công lên website khác
                          </div>
                        </button>
                        {wordpressSites.map((site) => (
                          <button
                            key={site.id}
                            onClick={() => handleSelectWordpressSite(site)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          >
                            <div className="font-medium">{site.siteName}</div>
                            <div className="text-sm text-gray-500">
                              {site.url}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p className="mb-2 text-sm">
                      ✈️ Bạn có thể tạo bài viết mà không cần chọn website.
                    </p>
                    <p className="text-xs text-gray-400">
                      Sau khi tạo xong, sao chép nội dung để đăng thủ công lên website của bạn.
                    </p>
                    <p className="mt-2">
                      <a href="/profile" className="underline text-blue-600 text-sm">
                        Thêm website WordPress
                      </a>{" "}
                      <span className="text-xs text-gray-400">để đăng tự động</span>
                    </p>
                  </div>
                )}

                {/* Cảnh báo khi website chưa cài plugin SEO Connector */}
                {selectedWordpressSite && !selectedWordpressSite.seoConnectorInstalled && (
                  <div className="mt-2 p-3.5 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
                    <div className="flex items-start gap-2.5">
                      <span className="text-lg leading-none">⚠️</span>
                      <div className="flex-1">
                        <p className="text-sm text-amber-800 font-semibold">
                          Website này chưa kích hoạt AutoWork SEO Connector
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Thẻ Tiêu đề SEO, Mô tả (Meta Description) và Từ khóa chính (Yoast SEO/Rank Math) sẽ không được tự động đồng bộ sang WordPress.
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <a
                            href="/profile?tab=wordpress"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold underline inline-flex items-center gap-1"
                          >
                            Xem hướng dẫn cài đặt (mở tab mới)
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              const siteIdBefore = selectedWordpressSite.id;
                              const siteForCheck = {
                                url: selectedWordpressSite.url,
                                username: selectedWordpressSite.username,
                                password: selectedWordpressSite.password,
                              };
                              toast.info("Đang kiểm tra lại trạng thái kết nối...", { autoClose: 1500 });
                              try {
                                const connectorResult = await checkSeoConnector(siteForCheck);
                                const db = getDatabase();
                                const userId = localStorage.getItem("userId");
                                if (!userId || !siteIdBefore) {
                                  await loadWordPressSites();
                                  return;
                                }
                                const siteRef = ref(db, `/users/${userId}/wordpressSites/${siteIdBefore}`);
                                const installedNow = Boolean(
                                  connectorResult?.success && connectorResult?.data?.installed
                                );
                                const pluginVersion =
                                  connectorResult?.data?.pluginVersion ||
                                  selectedWordpressSite.seoConnectorVersion ||
                                  null;
                                const patch = {
                                  seoConnectorInstalled: installedNow,
                                  seoConnectorCheckedAt: new Date().toISOString(),
                                  updatedAt: new Date().toISOString(),
                                };
                                if (installedNow && pluginVersion) {
                                  patch.seoConnectorVersion = pluginVersion;
                                }
                                await update(siteRef, patch);
                                const snapshot = await get(siteRef);
                                if (snapshot.exists()) {
                                  const updatedSite = { id: siteIdBefore, ...snapshot.val() };
                                  setSelectedWordpressSite(updatedSite);
                                } else {
                                  await loadWordPressSites();
                                }
                                if (installedNow) {
                                  toast.success("Tuyệt vời! Đã kết nối SEO thành công.");
                                } else {
                                  toast.error("Vẫn chưa tìm thấy plugin AutoWork SEO Connector. Hãy đảm bảo bạn đã Active plugin trên WordPress.");
                                }
                              } catch (error) {
                                console.error("Re-check SEO Connector failed:", error);
                                toast.error("Không thể kiểm tra lại SEO Connector. Vui lòng thử lại sau.");
                                await loadWordPressSites();
                              }
                            }}
                            className="text-xs text-green-600 hover:text-green-800 font-semibold underline inline-flex items-center gap-1"
                          >
                            🔄 Đã cài xong, làm mới
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Thông báo khi không chọn website */}
                {!selectedWordpressSite && wordpressSites.length > 0 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                      ℹ️ Bạn đang tạo bài viết để <strong>sao chép thủ công</strong>. Bài viết sẽ không được đăng tự động lên website nào.
                    </p>
                  </div>
                )}
              </div>

              {/* WordPress Status */}
              {selectedWordpressSite && (
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Trạng thái bài viết
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        wordpressConfig.status === "draft"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => handleWordpressStatusChange("draft")}
                    >
                      Bản nháp
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        wordpressConfig.status === "publish"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => handleWordpressStatusChange("publish")}
                    >
                      Công khai
                    </button>
                  </div>
                </div>
              )}

              {/* WordPress Categories */}
              {selectedWordpressSite && (
                <div className="mb-6">
                  {/* Header với toggle switch */}
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-700 text-sm font-medium">
                      Danh mục bài viết
                    </label>
                    <div className="relative inline-block w-12 align-middle select-none">
                      <input
                        type="checkbox"
                        name="categoryToggle"
                        id="categoryToggle"
                        checked={categoryMode !== "disabled"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCategoryMode("manual"); // Bật -> default là manual
                            // Reset categories về rỗng để dropdown hiển thị "Chọn danh mục"
                            setWordpressConfig((prev) => ({
                              ...prev,
                              categories: [],
                            }));
                          } else {
                            setCategoryMode("disabled"); // Tắt
                          }
                        }}
                        className="hidden"
                      />
                      <label
                        htmlFor="categoryToggle"
                        className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${
                          categoryMode !== "disabled"
                            ? "bg-blue-600"
                            : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            categoryMode !== "disabled"
                              ? "translate-x-6 bg-white"
                              : "translate-x-0 bg-white"
                          }`}
                        />
                      </label>
                    </div>
                  </div>

                  {categoryMode === "disabled" ? (
                    <p className="text-sm text-gray-500">
                      Bật để chọn danh mục cho bài viết.
                    </p>
                  ) : (
                    <>
                      {/* Chế độ chọn danh mục: thủ công hoặc AI */}
                      <div className="flex w-full rounded-md shadow-sm mb-3">
                        {["manual", "ai"].map((mode, index, arr) => (
                          <button
                            key={mode}
                            type="button"
                            className={`flex-1 py-2 px-2 text-center text-sm font-medium
                            ${index === 0 ? "rounded-l-md" : ""}
                            ${index === arr.length - 1 ? "rounded-r-md" : ""}
                            ${
                              categoryMode === mode
                                ? "bg-blue-600 text-white z-10"
                                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                            }`}
                            onClick={() => setCategoryMode(mode)}
                          >
                            {mode === "manual"
                              ? "Tự chọn danh mục"
                              : "AI tự chọn danh mục"}
                          </button>
                        ))}
                      </div>

                      {categoryMode === "ai" ? (
                        <p className="text-sm text-gray-500">
                          AI sẽ tự chọn hoặc tạo danh mục phù hợp dựa trên tiêu
                          đề bài viết sau khi đăng.
                        </p>
                      ) : (
                        <>
                          {isLoadingCategories ? (
                            <div className="text-sm text-gray-500">
                              Đang tải danh mục từ website WordPress...
                            </div>
                          ) : availableCategories.length > 0 ? (
                            <div className="relative" ref={categoryDropdownRef}>
                              <button
                                type="button"
                                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                onClick={() =>
                                  setIsCategoryDropdownOpen((prev) => !prev)
                                }
                              >
                                <span className="truncate">
                                  {(() => {
                                    const currentId =
                                      wordpressConfig.categories &&
                                      wordpressConfig.categories.length > 0
                                        ? wordpressConfig.categories[0]
                                        : null;
                                    const currentCat = availableCategories.find(
                                      (c) => c.id === currentId
                                    );
                                    return currentCat
                                      ? currentCat.name
                                      : "-- Chọn danh mục --";
                                  })()}
                                </span>
                                <span className="ml-2 text-gray-400 text-xs">
                                  ▼
                                </span>
                              </button>
                              {isCategoryDropdownOpen && (
                                <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg text-sm">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setWordpressConfig((prev) => ({
                                        ...prev,
                                        categories: [],
                                      }));
                                      setIsCategoryDropdownOpen(false);
                                    }}
                                    className="w-full px-3 py-2 text-left text-gray-500 hover:bg-gray-50"
                                  >
                                    -- Chọn danh mục --
                                  </button>
                                  {availableCategories.map((cat) => (
                                    <button
                                      key={cat.id}
                                      type="button"
                                      onClick={() => {
                                        setWordpressConfig((prev) => ({
                                          ...prev,
                                          categories: [cat.id],
                                        }));
                                        setIsCategoryDropdownOpen(false);
                                      }}
                                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${
                                        wordpressConfig.categories &&
                                        wordpressConfig.categories[0] === cat.id
                                          ? "bg-blue-50 text-blue-700"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      {cat.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">
                              Chưa tải được danh mục từ website. Bạn có thể nhập
                              tay để tạo mới.
                            </p>
                          )}

                          {categoriesError && (
                            <p className="mt-2 text-sm text-red-600">
                              {categoriesError}
                            </p>
                          )}

                          <div className="mt-3 flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Nhập category mới (ví dụ: Tin tức bảo mật)"
                              value={newCategoryName}
                              onChange={(e) =>
                                setNewCategoryName(e.target.value)
                              }
                            />
                            <button
                              type="button"
                              onClick={handleCreateCategory}
                              disabled={isCreatingCategory}
                              className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center ${
                                isCreatingCategory
                                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                                  : "bg-green-600 text-white hover:bg-green-700"
                              }`}
                            >
                              {isCreatingCategory ? (
                                <span>Đang tạo...</span>
                              ) : (
                                <>
                                  <FiPlus className="mr-1" size={14} />
                                  Thêm category
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {selectedWordpressSite && (
                <div className="mb-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                    <div>
                      <label className="text-gray-700 font-medium">
                        Dùng sản phẩm WooCommerce
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Lấy sản phẩm từ website đã chọn để AI chèn internal link
                        thật và ưu tiên ảnh sản phẩm.
                      </p>
                    </div>
                    <div className="relative inline-block align-middle select-none">
                      <input
                        type="checkbox"
                        name="wooProducts"
                        id="wooProducts"
                        checked={useWooProducts}
                        onChange={handleWooProductToggle}
                        className="hidden"
                      />
                      <label
                        htmlFor="wooProducts"
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                        style={{
                          backgroundColor: useWooProducts
                            ? "#3b82f6"
                            : "#e5e7eb",
                        }}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                            useWooProducts ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </label>
                    </div>
                  </div>

                  {useWooProducts && (
                    <div className="mt-3 p-4 border border-amber-200 bg-amber-50 rounded-md">
                      <div className="flex items-start justify-between gap-3 mb-4 p-3 bg-white border border-amber-200 rounded-md">
                        <div>
                          <label className="text-sm font-medium text-gray-800">
                            Cho phép AI chèn link sản phẩm
                          </label>
                          <p className="mt-1 text-sm text-gray-600">
                            {allowWooProductLinks
                              ? "AI được phép chèn internal link sản phẩm thật vào bài viết."
                              : "AI chỉ dùng dữ liệu sản phẩm làm ngữ cảnh và ưu tiên ảnh, không chèn link sản phẩm."}
                          </p>
                        </div>
                        <div className="relative inline-block align-middle select-none">
                          <input
                            type="checkbox"
                            name="allowWooProductLinks"
                            id="allowWooProductLinks"
                            checked={allowWooProductLinks}
                            onChange={() =>
                              setAllowWooProductLinks((prev) => !prev)
                            }
                            className="hidden"
                          />
                          <label
                            htmlFor="allowWooProductLinks"
                            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                            style={{
                              backgroundColor: allowWooProductLinks
                                ? "#3b82f6"
                                : "#e5e7eb",
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                allowWooProductLinks
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <div className="text-sm text-amber-900">
                          <div className="flex items-center gap-2 font-medium">
                            <FiPackage size={16} />
                            <span>
                              {selectedWooProducts.length > 0
                                ? `Đã chọn 1/${totalWooProducts} sản phẩm`
                                : totalWooProducts > 0
                                  ? `Đã tải ${totalWooProducts} sản phẩm`
                                  : "Chưa chọn sản phẩm nào"}
                            </span>
                          </div>
                          <p className="mt-1 text-amber-800">
                            Khi bật chế độ này, bạn cần chọn đúng 1 sản phẩm.
                          </p>
                          {wooProductsMeta.fetchedAll && totalWooProducts > 0 && (
                            <p className="mt-1 text-amber-700">
                              Hệ thống đã tải toàn bộ {totalWooProducts} sản phẩm
                              từ website để bạn tìm và chọn chính xác hơn.
                            </p>
                          )}
                          <p className="mt-1 text-amber-700">
                            {allowWooProductLinks
                              ? "Link sản phẩm sẽ được AI chèn tự nhiên trong nội dung nếu phù hợp."
                              : "Bài viết sẽ không chứa link sản phẩm, nhưng vẫn có thể ưu tiên ảnh từ sản phẩm đã chọn."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => fetchWooProductsForSite(selectedWordpressSite)}
                          disabled={isLoadingWooProducts}
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                            isLoadingWooProducts
                              ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                              : "bg-amber-600 text-white hover:bg-amber-700"
                          }`}
                        >
                          <FiRefreshCw
                            className={`mr-2 ${
                              isLoadingWooProducts ? "animate-spin" : ""
                            }`}
                            size={14}
                          />
                          {isLoadingWooProducts
                            ? "Đang tải sản phẩm..."
                            : hasLoadedWooProducts
                            ? "Tải lại sản phẩm"
                            : "Lấy danh sách sản phẩm"}
                        </button>
                      </div>

                      {hasLoadedWooProducts && wooProducts.length > 0 && (
                        <div className="relative mb-3">
                          <FiSearch
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                            aria-hidden
                          />
                          <input
                            type="search"
                            value={wooProductSearchQuery}
                            onChange={(e) =>
                              setWooProductSearchQuery(e.target.value)
                            }
                            placeholder="Tìm kiếm sản phẩm..."
                            className="w-full rounded-md border border-amber-200 bg-white py-2 pl-10 pr-3 text-sm text-gray-800 shadow-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                            autoComplete="off"
                            aria-label="Tìm kiếm sản phẩm"
                          />
                        </div>
                      )}

                      {wooProductsError && (
                        <div className="mb-3 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-700">
                          {wooProductsError}
                        </div>
                      )}

                      {isLoadingWooProducts && (
                        <div className="text-sm text-gray-600">
                          Đang tải danh sách sản phẩm WooCommerce từ website...
                        </div>
                      )}

                      {!isLoadingWooProducts &&
                        hasLoadedWooProducts &&
                        wooProducts.length === 0 &&
                        !wooProductsError && (
                          <div className="p-3 border border-dashed border-amber-300 rounded-md text-sm text-amber-900 bg-white">
                            Không tìm thấy sản phẩm WooCommerce public trên
                            website này.
                          </div>
                        )}

                      {!isLoadingWooProducts && wooProducts.length > 0 && (
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                          {filteredWooProducts.length === 0 && (
                            <div className="p-3 rounded-md border border-amber-200 bg-white text-sm text-amber-900">
                              Không có sản phẩm nào khớp với từ khóa tìm kiếm.
                            </div>
                          )}
                          {filteredWooProducts.map((product) => {
                            const isSelected = selectedWooProductIds.some(
                              (id) => String(id) === String(product.id)
                            );

                            return (
                              <label
                                key={product.id}
                                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                  isSelected
                                    ? "border-blue-300 bg-blue-50"
                                    : "border-gray-200 bg-white hover:bg-gray-50"
                                }`}
                              >
                                <input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() =>
                                    handleWooProductSelectionChange(product.id)
                                  }
                                  name="woo-product-selection"
                                  className="mt-1 h-4 w-4 text-blue-600"
                                />

                                {product.image?.thumbnail || product.image?.src ? (
                                  <img
                                    src={
                                      product.image.thumbnail ||
                                      product.image.src
                                    }
                                    alt={product.image.alt || product.name}
                                    className="w-16 h-16 rounded-md object-cover border border-gray-200 bg-white"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-md border border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400">
                                    <FiPackage size={20} />
                                  </div>
                                )}

                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                    <p className="font-medium text-gray-800">
                                      {product.name}
                                    </p>
                                    {product.priceText && (
                                      <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                                        {product.priceText}
                                      </span>
                                    )}
                                  </div>

                                  {product.shortDescription && (
                                    <p className="mt-1 text-sm text-gray-600">
                                      {product.shortDescription}
                                    </p>
                                  )}

                                  <a
                                    href={product.permalink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block text-xs text-blue-600 hover:text-blue-800 break-all"
                                  >
                                    {product.permalink}
                                  </a>

                                  {isSelected && allowWooProductLinks && (
                                    <div className="mt-2 inline-flex items-center text-xs text-green-700 font-medium">
                                      <FiCheck className="mr-1" size={13} />
                                      Đã chọn làm internal product link
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Độ dài bài viết */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Độ dài bài viết
                </label>
                <div className="flex w-full rounded-md shadow-sm">
                  {WORDPRESS_POST_LENGTHS.map(
                    ({ value, label }, index, arr) => (
                      <button
                        key={value}
                        type="button"
                        className={`flex-1 py-2 px-2 text-center text-sm font-medium
                        ${index === 0 ? "rounded-l-md" : ""}
                        ${index === arr.length - 1 ? "rounded-r-md" : ""}
                        ${
                          postLength === value
                            ? "bg-blue-600 text-white z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                        }`}
                        onClick={() => setPostLength(value)}
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Phong cách bài viết */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Phong cách bài viết
                </label>
                <div className="flex flex-wrap gap-2">
                  {POST_STYLES.map((style) => (
                    <button
                      key={style.value}
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                        ${
                          postStyles.includes(style.value)
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      onClick={() => {
                        setPostStyles((prev) =>
                          prev.includes(style.value)
                            ? prev.filter((s) => s !== style.value)
                            : [...prev, style.value]
                        );
                      }}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Loại bài viết (Content Format) */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Loại bài viết
                  <span className="text-sm text-gray-500 font-normal ml-2">
                    (Định dạng nội dung)
                  </span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {CONTENT_FORMAT_OPTIONS.map((format) => (
                    <button
                      key={format.value}
                      type="button"
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border
                        ${
                          contentFormat === format.value
                            ? "bg-blue-50 border-blue-500 text-blue-700 shadow-sm"
                            : "bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50"
                        }`}
                      onClick={() => setContentFormat(format.value)}
                      title={format.desc}
                    >
                      <div className="text-base leading-none mb-1">
                        {format.label}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {format.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dùng thông tin website */}
              {selectedWordpressSite && selectedWordpressSite.websiteInfo && (
                <div className="mb-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div>
                      <label className="text-gray-700 font-medium">
                        Dùng thông tin website
                      </label>
                      <p className="text-sm text-gray-500 mt-1">
                        Sử dụng thông tin mô tả website để tạo nội dung phù hợp
                        hơn
                      </p>
                    </div>
                    <div className="relative inline-block align-middle select-none">
                      <input
                        type="checkbox"
                        name="websiteInfo"
                        id="websiteInfo"
                        checked={useWebsiteInfo}
                        onChange={handleUseWebsiteInfoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="websiteInfo"
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                        style={{
                          backgroundColor: useWebsiteInfo
                            ? "#3b82f6"
                            : "#e5e7eb",
                        }}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                            useWebsiteInfo ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </label>
                    </div>
                  </div>
                  {useWebsiteInfo && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      {selectedWordpressSite.websiteInfo ? (
                        <p className="text-sm text-blue-800">
                          <strong>✓ Thông tin website sẽ được sử dụng:</strong>{" "}
                          {selectedWordpressSite.websiteInfo}
                        </p>
                      ) : (
                        <p className="text-sm text-orange-800">
                          <strong>⚠️ Cảnh báo:</strong> Website này chưa có
                          thông tin mô tả. Vui lòng thêm thông tin website trong
                          phần Thiết lập thông tin.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Thêm thông tin liên hệ */}
              {selectedWordpressSite && (
                <div className="mb-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-2">
                      <label className="text-gray-700 font-medium">
                        Thêm thông tin liên hệ
                      </label>
                      <SettingTooltip
                        label="Thêm thông tin liên hệ"
                        text={WEBSITE_CONTACT_TOGGLE_TOOLTIP_TEXT}
                      />
                    </div>
                    <div className="relative inline-block align-middle select-none">
                      <input
                        type="checkbox"
                        name="includeContactInfo"
                        id="includeContactInfo"
                        checked={includeContactInfo}
                        onChange={handleIncludeContactInfoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="includeContactInfo"
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                        style={{
                          backgroundColor: includeContactInfo
                            ? "#3b82f6"
                            : "#e5e7eb",
                        }}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                            includeContactInfo ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </label>
                    </div>
                  </div>

                </div>
              )}

              {/* Yêu cầu bài viết */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nhập yêu cầu bài viết
                  <span className="text-sm text-gray-500 font-normal ml-2">
                    (Tùy chọn - có thể để trống nếu có URL)
                  </span>
                  {selectedTopic && (
                    <span className="text-sm text-blue-600 font-normal ml-2">
                      (Đã chọn từ gợi ý chủ đề)
                    </span>
                  )}
                </label>
                <div className="relative">
                  <textarea
                    className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedTopic ? "bg-blue-50 border-blue-300" : ""
                    }`}
                    rows="4"
                    value={requirements}
                    onChange={(e) => {
                      setRequirements(e.target.value);
                      // Clear selected topic if user manually edits
                      if (selectedTopic && e.target.value !== selectedTopic) {
                        setSelectedTopic("");
                      }
                    }}
                    placeholder="Nhập yêu cầu bài viết hoặc để trống nếu chỉ dùng URL..."
                  ></textarea>
                  {requirements && (
                    <button
                      onClick={() => {
                        setRequirements("");
                        setSelectedTopic("");
                      }}
                      className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                      title="Xóa nội dung"
                    >
                      <FiXCircle size={20} />
                    </button>
                  )}
                  <div className="flex justify-between items-center mt-1 text-xs">
                    <span className="text-gray-400">
                      Gợi ý: Cung cấp thông tin chi tiết để AI viết hay hơn.
                    </span>
                    <span className={`font-semibold ${
                      requirements.length > 8000
                        ? "text-red-500"
                        : requirements.length > 6000
                        ? "text-orange-500"
                        : "text-gray-400"
                    }`}>
                      {requirements.length} / 8000 ký tự
                    </span>
                  </div>
                </div>
              </div>

              {shouldShowImageSourceSection && (
                <div className="mb-6 p-4 bg-gray-50 rounded-md">
                  <label className="block text-gray-700 font-medium mb-2">
                    Nguồn ảnh cho bài viết
                  </label>

                  <div className="space-y-2">
                    {Object.entries(WEBSITE_IMAGE_SOURCE_LABELS).map(
                      ([value, label]) => {
                        const isAILocked =
                          value === IMAGE_SOURCES.AI && userMembership < 3;

                        return (
                          <label
                            key={value}
                            className={`flex items-center p-2 rounded-md transition-colors duration-150 ${
                              imageSource === value
                                ? "bg-blue-50 border border-blue-300"
                                : "hover:bg-gray-100"
                            } ${
                              isAILocked
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="radio"
                              name="imageSource"
                              value={value}
                              checked={imageSource === value}
                              onChange={() => handleImageSourceChange(value)}
                              className="mr-3 text-blue-600 focus:ring-blue-500"
                              disabled={isAILocked}
                            />
                            <span className="text-gray-700">{label}</span>
                            {isAILocked && (
                              <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full">
                                🔒 Cao Cấp
                              </span>
                            )}
                          </label>
                        );
                      }
                    )}
                  </div>

                  {/* Số lượng ảnh — chỉ hiển thị cho Internet */}
                  {imageSource === IMAGE_SOURCES.INTERNET && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="block text-sm text-gray-600 mb-2">
                        📷 Số lượng ảnh trong bài viết
                      </label>
                      <div className="flex gap-2">
                        {WP_IMAGE_COUNT_OPTIONS.map((count) => (
                          <button
                            key={`wp-img-count-${count}`}
                            type="button"
                            onClick={() => setWpImageCount(count)}
                            className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                              wpImageCount === count
                                ? "bg-blue-100 border border-blue-400 text-blue-700"
                                : "bg-white border border-gray-300 hover:bg-gray-50 text-gray-600"
                            }`}
                          >
                            {count} ảnh
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Số lượng ảnh minh họa được chèn vào bài viết WordPress
                      </p>
                    </div>
                  )}

                  {imageSource === IMAGE_SOURCES.AI && (
                    <div className="mt-3 space-y-3 pl-2 border-l-2 border-blue-200">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Phong cách ảnh
                        </label>
                        <div className="relative" ref={styleDropdownRef}>
                          <button
                            type="button"
                            onClick={() =>
                              setIsStyleDropdownOpen(!isStyleDropdownOpen)
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center bg-white text-sm"
                          >
                            <span>
                              {IMAGE_STYLE_OPTIONS.find(
                                (opt) => opt.value === imageStyle
                              )?.label || "Chọn phong cách"}
                            </span>
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                isStyleDropdownOpen ? "rotate-180" : "rotate-0"
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              ></path>
                            </svg>
                          </button>
                          {isStyleDropdownOpen && (
                            <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                              <div className="flex flex-col">
                                {IMAGE_STYLE_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      handleImageStyleChange(opt.value);
                                      setIsStyleDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                                      imageStyle === opt.value
                                        ? "bg-blue-50 text-blue-700 font-medium"
                                        : ""
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Tỷ lệ ảnh
                        </label>
                        <div className="flex gap-2">
                          {aspectRatioOptions.map((opt) => (
                            <label
                              key={opt.value}
                              className={`flex-1 text-center p-2 rounded-md cursor-pointer text-sm transition-colors ${
                                aspectRatio === opt.value
                                  ? "bg-blue-100 border border-blue-400 text-blue-700"
                                  : "bg-white border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="radio"
                                name="aspectRatio"
                                value={opt.value}
                                checked={aspectRatio === opt.value}
                                onChange={() =>
                                  handleAspectRatioChange(opt.value)
                                }
                                className="hidden"
                              />
                              {opt.label}
                              <span className="block text-xs text-gray-500">
                                {opt.desc}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Yêu cầu thêm (tùy chọn)
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="2"
                          value={customImagePrompt}
                          onChange={handleCustomImagePromptChange}
                          maxLength={500}
                          placeholder="VD: tone ấm, có cây xanh, phong cách vintage..."
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 AI sẽ tự tạo mô tả chi tiết từ yêu cầu của bạn
                        </p>
                      </div>
                    </div>
                  )}

                  {imageSource === IMAGE_SOURCES.INTERNET &&
                    [3, 4, 6].includes(userMembership) && (
                      <div className="mt-3 pl-2 border-l-2 border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            Kiểm duyệt ảnh
                          </span>
                          <div className="relative inline-block align-middle select-none">
                            <input
                              type="checkbox"
                              name="imageValidation"
                              id="imageValidation"
                              checked={imageValidationEnabled}
                              onChange={handleImageValidationChange}
                              className="hidden"
                            />
                            <label
                              htmlFor="imageValidation"
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                              style={{
                                backgroundColor: imageValidationEnabled
                                  ? "#3b82f6"
                                  : "#e5e7eb",
                              }}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                                  imageValidationEnabled
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </label>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Khi bật, hệ thống ưu tiên lọc ảnh internet phù hợp và
                          an toàn hơn trước khi chèn vào bài viết.
                        </p>
                      </div>
                    )}

                  {isUploadSourceActive && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <label className="block text-gray-700 font-medium mb-2">
                        Ảnh tự cung cấp (Tối đa {MAX_USER_IMAGES} ảnh)
                        <span className="text-xs text-gray-500 font-normal ml-2">
                          {userImages.length}/{MAX_USER_IMAGES} ảnh đã thêm
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Tất cả ảnh bạn thêm sẽ được chèn vào bài viết WordPress.
                      </p>

                      {isUploadSourceMissingImages && (
                        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-sm text-amber-800">
                            Vui lòng thêm ít nhất 1 ảnh khi chọn nguồn ảnh tự cung cấp.
                          </p>
                        </div>
                      )}

                      {userImages.length >= MAX_USER_IMAGES && (
                        <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                          <p className="text-sm text-orange-800">
                            ⚠️ Đã đạt tối đa {MAX_USER_IMAGES} ảnh. Xóa ảnh cũ để thêm ảnh mới.
                          </p>
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="block text-sm text-gray-700 mb-2">
                          Upload ảnh từ máy tính:
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageFileUpload}
                            disabled={isCompressingImage || userImages.length >= MAX_USER_IMAGES}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          {isCompressingImage && (
                            <div className="absolute top-3 right-3">
                              <ClipLoader size={16} color="#3b82f6" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          📏 Max 1MB/ảnh (hệ thống tự động nén). Hỗ trợ: JPG, PNG,
                          GIF, WebP. Có thể chọn nhiều ảnh cùng lúc.
                        </p>
                      </div>

                      <div className="mb-3">
                        <label className="block text-sm text-gray-700 mb-2">
                          Hoặc nhập URL ảnh:
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={imageInputUrl}
                            onChange={(e) => {
                              setImageInputUrl(e.target.value);
                              if (imageInputErrors.length > 0) {
                                setImageInputErrors([]);
                              }
                            }}
                            disabled={isCompressingImage || userImages.length >= MAX_USER_IMAGES}
                            placeholder="https://example.com/image.jpg"
                            className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                              imageInputErrors.length > 0
                                ? "border-red-300 bg-red-50"
                                : "border-gray-300"
                            }`}
                          />
                          <button
                            onClick={handleAddImageUrl}
                            disabled={isCompressingImage || userImages.length >= MAX_USER_IMAGES}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isCompressingImage ? "⏳" : "Thêm"}
                          </button>
                        </div>
                        {imageInputErrors.length > 0 && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-xs text-red-700">
                              {imageInputErrors[0]}
                            </p>
                          </div>
                        )}
                      </div>

                      {userImages.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {userImages.map((image) => (
                            <div
                              key={image.id}
                              className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors"
                            >
                              <img
                                src={image.preview}
                                alt="Preview"
                                className="w-full aspect-square object-contain bg-gray-100"
                                onError={(e) => {
                                  e.target.src =
                                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' font-size='12' fill='%239ca3af'%3EError%3C/text%3E%3C/svg%3E";
                                }}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <button
                                  onClick={() => handleRemoveImage(image.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
                                  title="Xóa ảnh"
                                >
                                  <FiX size={16} />
                                </button>
                              </div>
                              {image.compressedSize && (
                                <div className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded truncate">
                                  {formatFileSize(image.compressedSize)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Số lượng ảnh sản phẩm WooCommerce */}
              {useWooProducts && (
                <div className="mb-6 p-4 bg-gray-50 rounded-md">
                  <label className="block text-gray-700 font-medium mb-2">
                    📷 Số lượng ảnh sản phẩm tối đa trong bài viết
                  </label>
                  <div className="flex gap-2">
                    {WP_IMAGE_COUNT_OPTIONS.map((count) => (
                      <button
                        key={`woo-img-count-${count}`}
                        type="button"
                        onClick={() => setWpImageCount(count)}
                        className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                          wpImageCount === count
                            ? "bg-blue-100 border border-blue-400 text-blue-700"
                            : "bg-white border border-gray-300 hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        {count} ảnh
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Nếu sản phẩm có ít hơn {wpImageCount} ảnh, hệ thống sẽ dùng
                    tất cả ảnh có sẵn của sản phẩm.
                  </p>
                </div>
              )}

              {/* URL tham khảo */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Nhập URL tham khảo (tùy chọn)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700 ${
                      urlValidationErrors.length > 0
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    value={inputUrls}
                    onChange={(e) => handleUrlsChange(e.target.value)}
                    onBlur={handleUrlsBlur}
                    placeholder="Nhập URL tham khảo... VD: https://example.com"
                  />
                  {inputUrls && (
                    <button
                      onClick={() => {
                        setInputUrls("");
                        setUrlValidationErrors([]);
                      }}
                      className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                      title="Xóa URLs"
                    >
                      <FiXCircle size={20} />
                    </button>
                  )}
                </div>

                {/* Hiển thị lỗi validation */}
                {urlValidationErrors.length > 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-red-400 mt-0.5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-red-800 mb-1">
                          Lỗi URL:
                        </h4>
                        <ul className="text-sm text-red-700 space-y-1">
                          {urlValidationErrors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hiển thị thông tin hữu ích */}
                {inputUrls && urlValidationErrors.length === 0 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-blue-400 mt-0.5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="text-sm text-blue-700">
                        <p className="mb-1">
                          <strong>URL hợp lệ được phát hiện.</strong>
                        </p>
                        <p className="text-xs text-blue-600">
                          {urlImageHint}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Progress bar cho job */}
              {isGeneratingPost && jobStatus && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">
                      {jobStatus === "pending" && "🔄 Đang khởi tạo job..."}
                      {jobStatus === "processing" && "⚡ Đang xử lý..."}
                    </span>
                    <span className="text-sm text-blue-600">
                      {jobProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.max(jobProgress, 5)}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-blue-600">
                    {jobStatus === "pending" &&
                      "Vui lòng chờ trong giây lát..."}
                    {jobStatus === "processing" &&
                      "AI đang tạo nội dung và xử lý ảnh..."}
                  </div>
                  {jobError && (
                    <div className="mt-2 text-xs text-red-600">
                      ❌ Lỗi: {jobError}
                    </div>
                  )}
                </div>
              )}

              {/* Progress bar cho publish job */}
              {isPublishing && publishJobStatus && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-800">
                      {publishJobStatus === "pending" &&
                        "🔄 Đang khởi tạo job đăng bài..."}
                      {publishJobStatus === "processing" &&
                        "📤 Đang đăng bài lên website..."}
                    </span>
                    <span className="text-sm text-green-600">
                      {publishJobProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.max(publishJobProgress, 5)}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-green-600">
                    {publishJobStatus === "pending" &&
                      "Vui lòng chờ trong giây lát..."}
                    {publishJobStatus === "processing" &&
                      "Đang upload, đăng bài và tối ưu Yoast SEO trên WordPress..."}
                  </div>
                  {publishJobError && (
                    <div className="mt-2 text-xs text-red-600">
                      ❌ Lỗi: {publishJobError}
                    </div>
                  )}
                </div>
              )}



              {/* === Premium Grounding: Google Search opt-in === */}
              <div className="mb-6">
                <GroundingToggle
                  useGoogleSearch={useGoogleSearch}
                  onChange={handleUseGoogleSearchChange}
                  isPlanEligible={userMembership >= 3}
                  contextLabel="bài viết WordPress"
                />
              </div>

                            {/* Nút tạo bài viết */}
              <div className="flex flex-col space-y-4">
                <div className="flex flex-row space-x-3">
                  <button
                    className="flex-1 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md flex items-center justify-center transition-colors disabled:opacity-60 disabled:pointer-events-none"
                    onClick={generatePost}
                    disabled={
                      isGeneratingPost ||
                      (userMembership !== 3 && userMembership !== 4 && userMembership !== 6) ||
                      (!hasFreeWebsiteQuota &&
                        userPoint < requiredWebsitePostPoints) ||
                      isUploadSourceMissingImages ||
                      requiresWooProductSelection(
                        useWooProducts,
                        selectedWooProductIds
                      )
                    }
                  >
                    {isGeneratingPost ? (
                      <div className="flex items-center">
                        <ClipLoader
                          size={20}
                          color="#ffffff"
                          className="mr-2"
                        />
                        <span>
                          {jobStatus === "pending" && "Đang tạo job..."}
                          {jobStatus === "processing" &&
                            `Đang tạo bài viết... ${jobProgress}%`}
                          {!jobStatus && "Đang tạo bài viết..."}
                        </span>
                      </div>
                    ) : (
                      <>
                        <FiGlobe className="mr-2 flex-shrink-0" />
                        <span>Tạo bài viết Website</span>
                        <span className="ml-2 rounded bg-white/15 px-2 py-0.5 text-xs font-semibold leading-none">
                          {websitePostChargeLabel}
                        </span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={suggestTopics}
                    disabled={
                      isGeneratingPost ||
                      isSuggestingTopics ||
                      (userMembership !== 3 && userMembership !== 4 && userMembership !== 6)
                    }
                    className="flex-1 items-center justify-center bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 transition-colors font-medium flex"
                  >
                    <FiRefreshCw
                      className={`mr-2 ${
                        isSuggestingTopics ? "animate-spin" : ""
                      }`}
                    />
                    {isSuggestingTopics
                      ? "Đang gợi ý chủ đề..."
                      : "Gợi ý chủ đề"}
                  </button>
                </div>
              </div>
            </div>

            {suggestedTopics.length > 0 && (
              <div className="relative bg-white rounded-md shadow-md p-5 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Chọn chủ đề
                </h2>
                <ul className="space-y-2 mb-4">
                  {suggestedTopics.map((topic, index) => (
                    <li
                      key={index}
                      onClick={() => handleTopicSelect(topic)}
                      className={`cursor-pointer p-2 rounded-md hover:bg-gray-50 ${
                        selectedTopic === topic
                          ? "bg-blue-50 border-l-4 border-blue-500 font-medium"
                          : ""
                      }`}
                    >
                      {topic}
                    </li>
                  ))}
                </ul>
                <FiXCircle
                  onClick={clearSuggestedTopics}
                  className="absolute top-4 right-4 text-red-600 cursor-pointer"
                  size={24}
                />
                <button
                  onClick={generatePost}
                  disabled={
                    isGeneratingPost ||
                    isSuggestingTopics ||
                    userMembership < 3 ||
                    (!hasFreeWebsiteQuota &&
                      userPoint < requiredWebsitePostPoints) ||
                    isUploadSourceMissingImages ||
                    requiresWooProductSelection(
                      useWooProducts,
                      selectedWooProductIds
                    )
                  }
                  className="flex items-center justify-center w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 mb-2 font-medium"
                >
                  <FiGlobe
                    className={`mr-2 ${isGeneratingPost ? "animate-spin" : ""}`}
                  />
                  {isGeneratingPost
                    ? jobStatus === "pending"
                      ? "Đang tạo job..."
                      : jobStatus === "processing"
                      ? `Đang tạo bài viết... ${jobProgress}%`
                      : "Đang tạo bài viết..."
                    : (
                      <>
                        <span>Tạo bài viết từ chủ đề được chọn</span>
                        <span className="ml-2 rounded bg-white/15 px-2 py-0.5 text-xs font-semibold leading-none">
                          {websitePostChargeLabel}
                        </span>
                      </>
                    )}
                </button>
                <button
                  onClick={suggestTopics}
                  disabled={
                    isGeneratingPost || isSuggestingTopics || userMembership < 3
                  }
                  className="flex items-center justify-center w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 font-medium"
                >
                  <FiRefreshCw
                    className={`mr-2 ${
                      isSuggestingTopics ? "animate-spin" : ""
                    }`}
                  />
                  {isSuggestingTopics
                    ? "Đang gợi ý chủ đề..."
                    : "Gợi ý chủ đề khác"}
                </button>
              </div>
            )}
          </div>

          {/* Phần bên phải - Kết quả */}
          <div className="w-full md:w-1/2">
            {generatedPost ? (
              <div className="bg-white rounded-md shadow-md overflow-hidden mb-20 md:mb-0">
                {/* Header với các nút action */}
                <div className="flex justify-between items-center p-5 border-b border-gray-200 bg-gray-50">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                      Xem trước bài viết
                      {/* Hiển thị indicator khi có URL được sử dụng */}
                      {validateUrls(inputUrls).urls.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Từ URL tham khảo
                        </span>
                      )}
                      {/* Hiển thị trạng thái điểm */}
                      {generatedPost.metadata?.pointsRefunded && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          💰 Đã hoàn điểm
                        </span>
                      )}
                    </h2>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                      <span>📝 Preview với định dạng WordPress chuẩn</span>
                      {themeInfo && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                          🎨 {themeInfo.name}
                        </span>
                      )}
                      {isLoadingThemeCSS && (
                        <span className="text-blue-600 text-xs flex items-center">
                          <ClipLoader
                            size={12}
                            color="#2563eb"
                            className="mr-1"
                          />
                          Đang tải theme...
                        </span>
                      )}
                    </div>
                  </div>
                  {!isEditMode && (
                    <div className="flex gap-2">
                      <button
                        onClick={openReportModal}
                        className="px-3 py-2.5 sm:py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-md font-medium text-sm flex items-center"
                        title="Báo cáo lỗi bài viết (Ctrl+R)"
                      >
                        <FiFlag className="mr-1" size={16} />
                        Báo cáo
                      </button>
                      <button
                        onClick={toggleEditMode}
                        className="px-3 py-2.5 sm:py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md font-medium text-sm flex items-center"
                      >
                        <FiEdit3 className="mr-1" size={16} />
                        Chỉnh sửa
                      </button>
                      <button
                        onClick={copyToClipboard}
                        className="px-3 py-2.5 sm:py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-md font-medium text-sm flex items-center"
                      >
                        <FiCopy className="mr-1" size={16} />
                        Sao chép
                      </button>
                    </div>
                  )}
                </div>

                {/* Website-like post preview */}
                <div className="bg-white">
                  {/* Post header - giống như header bài viết trên website */}
                  <div className="px-6 py-4 border-b border-gray-100">
                    {/* Post meta info */}
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium mr-3">
                        {selectedWordpressSite?.siteName || "Bài viết tự do"}
                      </span>
                      <span className="mr-3">
                        📅 {new Date().toLocaleDateString("vi-VN")}
                      </span>
                      <span className="mr-3">
                        🌐 {getLanguageName(selectedLanguage)}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        {selectedWordpressSite
                          ? (wordpressConfig.status === "publish"
                            ? "Công khai"
                            : "Bản nháp")
                          : "Copy thủ công"}
                      </span>
                    </div>

                    {/* Post title */}
                    {generatedPost.title && (
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-4">
                        {generatedPost.title}
                      </h1>
                    )}

                    {/* Meta Description Preview */}
                    {generatedPost.text && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-start">
                          <svg
                            className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-green-800 mb-1">
                              🔍 Meta Description (SEO):
                            </p>
                            <p className="text-sm text-green-700 leading-relaxed">
                              {(() => {
                                // Ưu tiên sử dụng meta description từ AI nếu có
                                if (generatedPost.metaDescription) {
                                  return generatedPost.metaDescription;
                                }
                                // Fallback: tạo từ text nếu không có meta description từ AI
                                const plainText = generatedPost.text
                                  .replace(/<[^>]*>/g, " ")
                                  .replace(/\s+/g, " ")
                                  .trim();
                                const maxLength = 158;
                                if (plainText.length <= maxLength) {
                                  return plainText;
                                }
                                // Cắt tại từ cuối cùng để tránh cắt giữa từ
                                const truncated = plainText.substring(
                                  0,
                                  maxLength
                                );
                                const lastSpaceIndex =
                                  truncated.lastIndexOf(" ");
                                if (lastSpaceIndex > 0) {
                                  return (
                                    truncated.substring(0, lastSpaceIndex) +
                                    "..."
                                  );
                                }
                                return truncated + "...";
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Categories and tags */}
                    {(wordpressConfig.categories.length > 0 ||
                      wordpressConfig.tags) && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {/* Categories */}
                        {wordpressConfig.categories.map((categoryId) => {
                          const category = availableCategories.find(
                            (cat) => cat.id === categoryId
                          );
                          return category ? (
                            <span
                              key={categoryId}
                              className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              📁 {category.name}
                            </span>
                          ) : null;
                        })}

                        {/* Tags */}
                        {wordpressConfig.tags &&
                          wordpressConfig.tags.split(",").map((tag, index) => (
                            <span
                              key={index}
                              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                            >
                              🏷️ {tag.trim()}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Post content */}
                  <div className="px-6 py-6">
                    {isEditMode ? (
                      <div className="mb-3">
                        <div className="relative">
                          <textarea
                            className="w-full p-4 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none overflow-hidden"
                            value={editedPostText}
                            onChange={(e) => {
                              setEditedPostText(e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height =
                                Math.max(100, e.target.scrollHeight) + "px";
                            }}
                            ref={(el) => {
                              if (el) {
                                el.style.height = "auto";
                                el.style.height =
                                  Math.max(100, el.scrollHeight) + "px";
                              }
                            }}
                            placeholder="Nhập nội dung bài viết..."
                          ></textarea>
                          <div className="absolute bottom-2 right-3 text-xs text-gray-500 bg-white px-1.5 py-0.5 rounded-full border border-gray-200">
                            {editedPostText.length} ký tự
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="text-xs text-gray-500 hidden sm:block">
                            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                              Ctrl
                            </kbd>{" "}
                            +{" "}
                            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                              Enter
                            </kbd>{" "}
                            để lưu •{" "}
                            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded">
                              Esc
                            </kbd>{" "}
                            để hủy
                          </div>
                          <div className="flex gap-2 ml-auto">
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium flex items-center"
                            >
                              <FiX className="mr-1" size={14} />
                              Hủy
                            </button>
                            <button
                              onClick={toggleEditMode}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center"
                            >
                              <FiCheck className="mr-1" size={14} />
                              Lưu
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <article
                        className="wordpress-content"
                        style={getWordPressPreviewStyles()}
                      >
                        <style jsx>{`
                          .wordpress-content {
                            font-family: -apple-system, BlinkMacSystemFont,
                              "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell,
                              "Helvetica Neue", sans-serif;
                            font-size: 16px;
                            line-height: 1.6;
                            color: #333333;
                            max-width: 100%;
                          }

                          /* WordPress default styles */
                          .wordpress-content p {
                            margin: 0 0 1.5em 0;
                            line-height: 1.6;
                            text-align: justify;
                          }

                          .wordpress-content h1 {
                            font-size: 2em;
                            margin: 0.67em 0;
                            font-weight: bold;
                            line-height: 1.2;
                            color: #333;
                          }

                          .wordpress-content h2 {
                            font-size: 1.5em;
                            margin: 0.83em 0;
                            font-weight: bold;
                            line-height: 1.3;
                            color: #333;
                          }

                          .wordpress-content h3 {
                            font-size: 1.17em;
                            margin: 1em 0;
                            font-weight: bold;
                            line-height: 1.4;
                            color: #333;
                          }

                          .wordpress-content h4 {
                            font-size: 1em;
                            margin: 1.33em 0;
                            font-weight: bold;
                            color: #333;
                          }

                          .wordpress-content h5 {
                            font-size: 0.83em;
                            margin: 1.67em 0;
                            font-weight: bold;
                            color: #333;
                          }

                          .wordpress-content h6 {
                            font-size: 0.67em;
                            margin: 2.33em 0;
                            font-weight: bold;
                            color: #333;
                          }

                          .wordpress-content ul,
                          .wordpress-content ol {
                            margin: 1em 0;
                            padding-left: 2em;
                          }

                          .wordpress-content ul {
                            list-style-type: disc;
                          }

                          .wordpress-content ol {
                            list-style-type: decimal;
                          }

                          .wordpress-content li {
                            margin: 0.5em 0;
                            line-height: 1.6;
                          }

                          .wordpress-content strong {
                            font-weight: bold;
                          }

                          .wordpress-content em {
                            font-style: italic;
                          }

                          .wordpress-content blockquote {
                            margin: 1em 0;
                            padding: 1em;
                            border-left: 4px solid #ddd;
                            background-color: #f9f9f9;
                            font-style: italic;
                          }

                          /* WordPress image blocks */
                          .wordpress-content .wp-block-image {
                            margin: 1em 0;
                            text-align: center;
                            display: block;
                            clear: both;
                          }

                          .wordpress-content .wp-block-image img {
                            height: auto;
                            max-width: 100%;
                            border-radius: 4px;
                            display: block;
                            margin: 0 auto;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                          }

                          .wordpress-content figure {
                            margin: 1em 0;
                            display: block;
                            text-align: center;
                          }

                          .wordpress-content .wp-image {
                            height: auto;
                            max-width: 100%;
                            display: block;
                            margin: 0 auto;
                          }

                          /* General img tag styling */
                          .wordpress-content img {
                            height: auto;
                            max-width: 100%;
                            display: block;
                            margin: 1em auto;
                            border-radius: 4px;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                          }

                          /* Error handling for images */
                          .wordpress-content img[src=""],
                          .wordpress-content img:not([src]) {
                            display: none;
                          }

                          /* Loading state for images */
                          .wordpress-content img[loading="lazy"] {
                            background-color: #f3f4f6;
                            min-height: 200px;
                          }

                          /* Image fallback styling */
                          .wordpress-content .image-error {
                            display: block;
                            padding: 2em;
                            background-color: #f9fafb;
                            border: 2px dashed #d1d5db;
                            border-radius: 8px;
                            text-align: center;
                            color: #6b7280;
                            margin: 1em 0;
                          }

                          /* Enhance figure styling */
                          .wordpress-content figure.wp-block-image {
                            background-color: #fafafa;
                            padding: 0.5em;
                            border-radius: 8px;
                            border: 1px solid #e5e7eb;
                          }

                          /* WordPress table styles */
                          .wordpress-content table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 1.5em 0;
                            font-size: 0.95em;
                            background-color: #fff;
                            border: 1px solid #e5e7eb;
                            border-radius: 8px;
                            overflow: hidden;
                          }

                          .wordpress-content table thead {
                            background-color: #f8fafc;
                          }

                          .wordpress-content table thead th {
                            background-color: #f1f5f9;
                            font-weight: 600;
                            color: #1e293b;
                            text-align: left;
                            padding: 0.75em 1em;
                            border-bottom: 2px solid #e2e8f0;
                            border-right: 1px solid #e5e7eb;
                            white-space: nowrap;
                          }

                          .wordpress-content table thead th:last-child {
                            border-right: none;
                          }

                          .wordpress-content table tbody td {
                            padding: 0.65em 1em;
                            border-bottom: 1px solid #f1f5f9;
                            border-right: 1px solid #f1f5f9;
                            color: #334155;
                            vertical-align: top;
                            line-height: 1.5;
                          }

                          .wordpress-content table tbody td:last-child {
                            border-right: none;
                          }

                          .wordpress-content table tbody tr:last-child td {
                            border-bottom: none;
                          }

                          .wordpress-content table tbody tr:nth-child(even) {
                            background-color: #f8fafc;
                          }

                          .wordpress-content table tbody tr:hover {
                            background-color: #f1f5f9;
                          }

                          .wordpress-content table caption {
                            padding: 0.5em;
                            font-style: italic;
                            color: #64748b;
                            caption-side: bottom;
                          }

                          /* Responsive table wrapper */
                          .wordpress-content .wp-block-table {
                            overflow-x: auto;
                            margin: 1.5em 0;
                            -webkit-overflow-scrolling: touch;
                          }

                          /* Table with bold first column */
                          .wordpress-content table td:first-child {
                            font-weight: 500;
                          }
                        `}</style>
                        {/* Inject actual WordPress theme CSS if available */}
                        {themeCSS && (
                          <style
                            dangerouslySetInnerHTML={{
                              __html: getContentAreaCSS(themeCSS),
                            }}
                          />
                        )}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: processContentForPreview(
                              generatedPost.text,
                              generatedPost.images,
                              generatedPost.title
                            ),
                          }}
                        />
                      </article>
                    )}
                  </div>

                  {/* Post footer - SEO và thông tin thêm */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    {/* Meta Description */}
                    {/* Meta Description feature has been removed */}

                    {/* Post stats */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-3">
                      <span>📝 Độ dài: {generatedPost.text.length} ký tự</span>
                      <span>
                        📖 Thời gian đọc: ~
                        {Math.ceil(generatedPost.text.length / 1000)} phút
                      </span>
                      <span>🎨 Phong cách: {postStyles.join(", ")}</span>
                      <span>
                        🖼️ Ảnh: {countPostImagesForPublish(generatedPost)} nguồn
                        sẵn sàng đăng
                      </span>
                    </div>

                    {/* Debug info - chỉ hiển thị trong development */}
                    {process.env.NODE_ENV === "development" && (
                      <details className="mb-3">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                          🔧 Debug Info (Development)
                        </summary>
                        <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                          <p>
                            <strong>Có placeholder ảnh AI:</strong>{" "}
                            {hasAutoworkImagePlaceholders(generatedPost.text)
                              ? "✅ Có"
                              : "❌ Không"}
                          </p>
                          <p>
                            <strong>Có ảnh inline render được:</strong>{" "}
                            {hasRenderableInlinePostImages(generatedPost.text)
                              ? "✅ Có"
                              : "❌ Không"}
                          </p>
                          <p>
                            <strong>Có nguồn ảnh publish:</strong>{" "}
                            {normalizeGeneratedImageSources(generatedPost.images)
                              .length > 0
                              ? `✅ Có (${
                                  normalizeGeneratedImageSources(
                                    generatedPost.images
                                  ).length
                                })`
                              : "❌ Không"}
                          </p>
                          <p>
                            <strong>Có URL ảnh:</strong>{" "}
                            {/(https?:\/\/[^\s<>"]+\.(jpg|jpeg|png|gif|webp|svg))/i.test(
                              generatedPost.text
                            )
                              ? "✅ Có"
                              : "❌ Không"}
                          </p>
                          <p>
                            <strong>Raw content preview:</strong>
                          </p>
                          <pre className="bg-white p-2 rounded mt-1 max-h-32 overflow-y-auto text-xs">
                            {generatedPost.text.substring(0, 500)}...
                          </pre>
                        </div>
                      </details>
                    )}

                    {/* User-friendly report reminder */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FiFlag className="h-4 w-4 text-orange-600 mr-2" />
                          <span className="text-sm text-orange-800">
                            <strong>Không hài lòng với bài viết?</strong>
                          </span>
                        </div>
                        <button
                          onClick={openReportModal}
                          className="text-sm text-orange-700 hover:text-orange-900 underline font-medium"
                        >
                          Báo cáo ngay
                        </button>
                      </div>
                      <p className="text-xs text-orange-600 mt-1 ml-6">
                        Giúp chúng tôi cải thiện AI tạo nội dung tốt hơn cho bạn
                      </p>

                      {/* Zalo Support Contact */}
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 mr-2 flex items-center justify-center">
                              <span className="text-sm">💬</span>
                            </div>
                            <span className="text-sm text-orange-800">
                              <strong>Cần hỗ trợ trực tiếp?</strong>
                            </span>
                          </div>
                          <a
                            href="https://zalo.me/0394932696"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                          >
                            <svg
                              className="w-4 h-4 mr-1.5"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                            </svg>
                            Zalo hỗ trợ
                          </a>
                        </div>
                        <p className="text-xs text-orange-600 mt-1 ml-6">
                          Liên hệ ngay để được tư vấn và hỗ trợ 1:1
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="p-5 bg-gray-50 border-t border-gray-200">
                  {selectedWordpressSite ? (
                    /* Có chọn website → hiển nút đăng bài */
                    <>
                      <button
                        onClick={publishPost}
                        className="flex items-center justify-center w-full gap-2 bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 font-medium transition-colors"
                        disabled={
                          isPublishing ||
                          !selectedWordpressSite ||
                          (userMembership !== 3 && userMembership !== 4 && userMembership !== 6)
                        }
                      >
                        {isPublishing ? (
                          <ClipLoader size={20} color={"#fff"} />
                        ) : (
                          <FiSend />
                        )}
                        {isPublishing
                          ? publishJobStatus === "pending"
                            ? "Đang tạo job..."
                            : publishJobStatus === "processing"
                            ? `Đang đăng và tối ưu SEO... ${publishJobProgress}%`
                            : "Đang đăng bài lên website..."
                          : "🚀 Đăng bài viết lên website"}
                      </button>
                      <p className="text-center text-sm text-gray-500 mt-2">
                        Sẽ đăng lên:{" "}
                        <strong>{selectedWordpressSite.siteName}</strong>
                      </p>
                    </>
                  ) : (
                    /* Không chọn website → hiển nút copy */
                    <>
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center justify-center w-full gap-2 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium transition-colors"
                      >
                        <FiCopy />
                        📋 Sao chép nội dung bài viết
                      </button>
                      <p className="text-center text-sm text-gray-500 mt-2">
                        Sao chép nội dung để đăng thủ công lên website của bạn
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-md shadow-md p-5 h-full flex flex-col items-center justify-center text-center">
                <div className="text-gray-400 mb-4">
                  <FiGlobe size={64} />
                </div>
                <h2 className="text-xl font-semibold text-gray-600 mb-2">
                  Chưa có bài viết nào
                </h2>
                <p className="text-gray-500">
                  Nhập yêu cầu và nhấn "Tạo bài viết Website" để bắt đầu
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FiFlag className="mr-2 text-orange-600" />
                  Báo cáo lỗi bài viết
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Giúp chúng tôi cải thiện chất lượng bài viết
                </p>
              </div>
              <button
                onClick={closeReportModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5">
              {/* Quick Report Types */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Chọn loại lỗi: <span className="text-red-500">*</span>
                </label>
                <div className="grid gap-2">
                  {reportTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setReportType(type.value)}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${
                        reportType === type.value
                          ? "border-orange-500 bg-orange-50 text-orange-900"
                          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Description */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mô tả chi tiết (tùy chọn):
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows="3"
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Mô tả thêm về lỗi để chúng tôi cải thiện tốt hơn..."
                  maxLength={500}
                />
                <div className="text-xs text-gray-400 mt-1 text-right">
                  {reportDescription.length}/500 ký tự
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                onClick={closeReportModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                disabled={isSubmittingReport}
              >
                Hủy
              </button>
              <button
                onClick={handleReportSubmit}
                disabled={!reportType || isSubmittingReport}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
              >
                {isSubmittingReport ? (
                  <>
                    <ClipLoader size={16} color="#ffffff" className="mr-2" />
                    Đang gửi...
                  </>
                ) : (
                  <>
                    <FiSend className="mr-2" size={16} />
                    Gửi báo cáo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Progress Indicator - hiển thị khi đang xử lý để người dùng luôn thấy dù scroll */}
      {(isGeneratingPost || isPublishing) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {/* Thin progress bar at very top */}
          <div
            style={{
              height: '3px',
              background: 'rgba(0,0,0,0.08)',
              width: '100%',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: '0 2px 2px 0',
                background: isPublishing
                  ? 'linear-gradient(90deg, #059669, #34d399)'
                  : 'linear-gradient(90deg, #2563eb, #60a5fa)',
                width: `${Math.max(
                  isPublishing ? publishJobProgress : jobProgress,
                  3
                )}%`,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>

          {/* Compact floating status badge */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '8px',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 18px',
                borderRadius: '9999px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#fff',
                background: isPublishing
                  ? 'linear-gradient(135deg, #059669, #047857)'
                  : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(8px)',
                animation: 'floatingPulse 2s ease-in-out infinite',
              }}
            >
              {/* Spinner dot */}
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  animation: 'floatingDot 1.2s ease-in-out infinite',
                }}
              />
              {/* Status text */}
              <span>
                {isPublishing
                  ? publishJobStatus === 'pending'
                    ? '🔄 Đang tạo job đăng bài...'
                  : publishJobStatus === 'processing'
                    ? `📤 Đang đăng và tối ưu SEO... ${publishJobProgress}%`
                    : '📤 Đang đăng bài lên website...'
                  : jobStatus === 'pending'
                  ? '🔄 Đang khởi tạo...'
                  : jobStatus === 'processing'
                  ? `⚡ Đang tạo bài viết... ${jobProgress}%`
                  : '⚡ Đang tạo bài viết...'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations for floating indicator */}
      {(isGeneratingPost || isPublishing) && (
        <style>{`
          @keyframes floatingPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.02); opacity: 0.92; }
          }
          @keyframes floatingDot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.6); }
          }
        `}</style>
      )}

      <ToastContainer />
    </div>
  );
};

export default WebsitePostGenerator;
